import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { GoogleMap, useJsApiLoader, OverlayView, InfoWindow } from "@react-google-maps/api";
import "./DashboardTheme.css";
import { getData } from "../../../Services/Services";
import {
  getincidentcatcreate,
  getThemeURL,
  getAllStates,
  getStateRegions,
} from "../../../Services/EndPoints";
import RegionHeatmapSelector, { buildRegionDropdownOptions } from "./RegionHeatmapSelector";
import RecentAlerts from "./Weatheralerts";

// ─── Import Essential Services (panel UI + shared data/helpers) ────────────────
import EssentialServicesPanel, {
  SERVICE_CATEGORIES,
  ALL_SUBCATEGORIES,
  CATEGORY_ANIM_CONFIG,
  DEFAULT_ANIM_CONFIG,
  fetchPlacesAcrossState,
} from "./EssentialServicesPanel";

// ─── Constants ─────────────────────────────────────────────────────────────────
const GOOGLE_MAPS_API_KEY = "AIzaSyDLzC-FhQmB8JS2H46OW46DNOEboBbrZVg";
const GOOGLE_MAPS_LIBRARIES = ["drawing", "geometry", "places", "visualization"];

const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };

// ─── Indian State Capitals (lat/lng) ───────────────────────────────────────────
const STATE_CAPITALS = {
  "andhra pradesh": { name: "Amaravati", lat: 16.5062, lng: 80.6480 },
  "arunachal pradesh": { name: "Itanagar", lat: 27.0844, lng: 93.6053 },
  "assam": { name: "Dispur", lat: 26.1433, lng: 91.7898 },
  "bihar": { name: "Patna", lat: 25.6093, lng: 85.1376 },
  "chhattisgarh": { name: "Raipur", lat: 21.2514, lng: 81.6296 },
  "goa": { name: "Panaji", lat: 15.4909, lng: 73.8278 },
  "gujarat": { name: "Gandhinagar", lat: 23.2156, lng: 72.6369 },
  "haryana": { name: "Chandigarh", lat: 30.7333, lng: 76.7794 },
  "himachal pradesh": { name: "Shimla", lat: 31.1048, lng: 77.1734 },
  "jharkhand": { name: "Ranchi", lat: 23.3441, lng: 85.3096 },
  "karnataka": { name: "Bengaluru", lat: 12.9716, lng: 77.5946 },
  "kerala": { name: "Thiruvananthapuram", lat: 8.5241, lng: 76.9366 },
  "madhya pradesh": { name: "Bhopal", lat: 23.2599, lng: 77.4126 },
  "maharashtra": { name: "Mumbai", lat: 19.0760, lng: 72.8777 },
  "manipur": { name: "Imphal", lat: 24.8170, lng: 93.9368 },
  "meghalaya": { name: "Shillong", lat: 25.5788, lng: 91.8933 },
  "mizoram": { name: "Aizawl", lat: 23.7271, lng: 92.7176 },
  "nagaland": { name: "Kohima", lat: 25.6751, lng: 94.1086 },
  "odisha": { name: "Bhubaneswar", lat: 20.2961, lng: 85.8245 },
  "punjab": { name: "Chandigarh", lat: 30.7333, lng: 76.7794 },
  "rajasthan": { name: "Jaipur", lat: 26.9124, lng: 75.7873 },
  "sikkim": { name: "Gangtok", lat: 27.3389, lng: 88.6065 },
  "tamil nadu": { name: "Chennai", lat: 13.0827, lng: 80.2707 },
  "telangana": { name: "Hyderabad", lat: 17.3850, lng: 78.4867 },
  "tripura": { name: "Agartala", lat: 23.8315, lng: 91.2868 },
  "uttar pradesh": { name: "Lucknow", lat: 26.8467, lng: 80.9462 },
  "uttarakhand": { name: "Dehradun", lat: 30.3165, lng: 78.0322 },
  "west bengal": { name: "Kolkata", lat: 22.5726, lng: 88.3639 },
  // Union Territories
  "andaman and nicobar islands": { name: "Port Blair", lat: 11.6234, lng: 92.7265 },
  "chandigarh": { name: "Chandigarh", lat: 30.7333, lng: 76.7794 },
  "dadra and nagar haveli and daman and diu": { name: "Daman", lat: 20.3974, lng: 72.8328 },
  "delhi": { name: "New Delhi", lat: 28.6139, lng: 77.2090 },
  "jammu and kashmir": { name: "Srinagar", lat: 34.0837, lng: 74.7973 },
  "ladakh": { name: "Leh", lat: 34.1526, lng: 77.5771 },
  "lakshadweep": { name: "Kavaratti", lat: 10.5593, lng: 72.6358 },
  "puducherry": { name: "Puducherry", lat: 11.9416, lng: 79.8083 },
};

/**
 * Look up the capital city for a given state name.
 * Falls back to null if no match found.
 */
const getCapitalForState = (stateName) => {
  if (!stateName) return null;
  const key = stateName.toLowerCase().trim();
  return STATE_CAPITALS[key] || null;
};

// Move this OUTSIDE the component (utility function)
const generateGridPoints = (stateGeoJSON, customStep = null) => {
  if (!stateGeoJSON) return [];
  const bounds = { latMin: 90, latMax: -90, lngMin: 180, lngMax: -180 };

  const processCoords = (coords) => {
    if (!Array.isArray(coords)) return;
    if (typeof coords[0] === "number") {
      bounds.lngMin = Math.min(bounds.lngMin, coords[0]);
      bounds.lngMax = Math.max(bounds.lngMax, coords[0]);
      bounds.latMin = Math.min(bounds.latMin, coords[1]);
      bounds.latMax = Math.max(bounds.latMax, coords[1]);
    } else {
      coords.forEach(processCoords);
    }
  };

  const geom = stateGeoJSON.geometry || stateGeoJSON;
  if (geom?.coordinates) processCoords(geom.coordinates);

  // Dynamically calculate step to target around 40 points for ultra-fast loading (< 5s)
  const width = bounds.lngMax - bounds.lngMin;
  const height = bounds.latMax - bounds.latMin;
  const area = width * height;

  // Calculate dynamic step if an explicit customStep wasn't provided
  // target roughly 40 points: area / step^2 = 40 => step = Math.sqrt(area / 40)
  const dynamicStep = area > 0 ? Math.max(0.15, Math.sqrt(area / 40)) : 0.75;
  const step = customStep || dynamicStep;

  const points = [];
  let idx = 0;
  for (let lat = bounds.latMin; lat <= bounds.latMax; lat = Math.round((lat + step) * 1000) / 1000) {
    for (let lng = bounds.lngMin; lng <= bounds.lngMax; lng = Math.round((lng + step) * 1000) / 1000) {
      points.push({
        name: `Grid_${idx++}`,
        lat: Math.round(lat * 10000) / 10000,
        lng: Math.round(lng * 10000) / 10000,
      });
    }
  }
  return points;
};

// ─── Module-level weather cache ────────────────────────────────────────────────
const _weatherCache = new Map();
let _gridWeatherCache = null;

// ─── Temperature Color Scale ───────────────────────────────────────────────────
const TEMP_COLOR_STOPS = [
  { temp: -25, color: [15, 30, 140] },
  { temp: -15, color: [30, 80, 200] },
  { temp: -10, color: [50, 140, 240] },
  { temp: -5, color: [70, 200, 240] },
  { temp: 0, color: [100, 220, 200] },
  { temp: 5, color: [120, 230, 150] },
  { temp: 10, color: [180, 240, 100] },
  { temp: 15, color: [230, 230, 60] },
  { temp: 20, color: [255, 200, 40] },
  { temp: 25, color: [255, 160, 30] },
  { temp: 30, color: [240, 100, 20] },
  { temp: 35, color: [220, 50, 15] },
  { temp: 40, color: [180, 20, 20] },
  { temp: 45, color: [130, 0, 30] },
  { temp: 50, color: [90, 0, 40] },
];

const getTempColor = (temp) => {
  if (temp === null || temp === undefined) return "rgba(128,128,128,0.5)";
  if (temp <= TEMP_COLOR_STOPS[0].temp) {
    const [r, g, b] = TEMP_COLOR_STOPS[0].color;
    return `rgba(${r},${g},${b},0.85)`;
  }
  if (temp >= TEMP_COLOR_STOPS[TEMP_COLOR_STOPS.length - 1].temp) {
    const [r, g, b] = TEMP_COLOR_STOPS[TEMP_COLOR_STOPS.length - 1].color;
    return `rgba(${r},${g},${b},0.85)`;
  }
  for (let i = 0; i < TEMP_COLOR_STOPS.length - 1; i++) {
    const lo = TEMP_COLOR_STOPS[i];
    const hi = TEMP_COLOR_STOPS[i + 1];
    if (temp >= lo.temp && temp <= hi.temp) {
      const t = (temp - lo.temp) / (hi.temp - lo.temp);
      const r = Math.round(lo.color[0] + t * (hi.color[0] - lo.color[0]));
      const g = Math.round(lo.color[1] + t * (hi.color[1] - lo.color[1]));
      const b = Math.round(lo.color[2] + t * (hi.color[2] - lo.color[2]));
      return `rgba(${r},${g},${b},0.85)`;
    }
  }
  return "rgba(128,128,128,0.5)";
};

// ─── Heatmap Layer Definitions ─────────────────────────────────────────────────
const HEATMAP_LAYERS = [
  { id: "temperature", label: "Temperature", icon: "🌡️", unit: "°C", colorFn: getTempColor, valueFn: (w) => w.temperature, metricFn: (v) => v?.toFixed(1) + "°C" },
  {
    id: "humidity", label: "Humidity", icon: "💧", unit: "%", colorFn: (v) => {
      if (v == null) return "rgba(128,128,128,0.5)";
      if (v >= 80) return "rgba(30,64,175,0.8)";
      if (v >= 60) return "rgba(59,130,246,0.75)";
      if (v >= 40) return "rgba(96,165,250,0.7)";
      return "rgba(191,219,254,0.65)";
    }, valueFn: (w) => w.humidity, metricFn: (v) => Math.round(v) + "%"
  },
  {
    id: "windspeed", label: "Wind Speed", icon: "🌬️", unit: "km/h", colorFn: (v) => {
      if (v == null) return "rgba(128,128,128,0.5)";
      if (v >= 20) return "rgba(220,38,38,0.85)";
      if (v >= 12) return "rgba(249,115,22,0.8)";
      if (v >= 7) return "rgba(234,179,8,0.7)";
      return "rgba(74,222,128,0.65)";
    }, valueFn: (w) => w.windspeed, metricFn: (v) => v?.toFixed(1) + " km/h"
  },
  {
    id: "rain", label: "Rain Prob.", icon: "🌧️", unit: "%", colorFn: (v) => {
      if (v == null) return "rgba(128,128,128,0.5)";
      if (v >= 80) return "rgba(30,58,138,0.85)";
      if (v >= 60) return "rgba(37,99,235,0.8)";
      if (v >= 30) return "rgba(96,165,250,0.7)";
      return "rgba(219,234,254,0.6)";
    }, valueFn: (w) => w.rain, metricFn: (v) => Math.round(v) + "%"
  },
  {
    id: "visibility", label: "Visibility", icon: "👁️", unit: "km",
    colorFn: (v) => {
      if (v == null) return "rgba(128,128,128,0.5)";
      if (v < 5) return "rgba(127,29,29,0.85)";
      if (v < 8) return "rgba(220,38,38,0.8)";
      if (v < 12) return "rgba(251,146,60,0.7)";
      return "rgba(134,239,172,0.65)";
    },
    metricFn: (v) => v?.toFixed(1) + " km"
  }
];

// ─── Weather Condition Definitions ─────────────────────────────────────────────
const WEATHER_CONDITIONS = [
  {
    id: "cold_wave", label: "Cold Wave", icon: "❄️", color: "#0EA5E9",
    accentColor: "#BAE6FD", bgColor: "rgba(14,165,233,0.12)",
    borderColor: "rgba(14,165,233,0.35)",
    gradient: "linear-gradient(135deg,#0EA5E9,#6366F1)",
    description: "Temp ≤ 5°C", heatmapLayer: "temperature",
    check: (w) => w.temperature <= 5,
    severity: (w) => w.rain > 80 && w.windspeed > 50 ? "extreme" : w.rain > 65 ? "high" : "moderate",
    metric: (w) => `${w.temperature?.toFixed(1)}°C | ${Math.round(w.rain)}%`,
    tip: "Regions experiencing temperatures ≤ 5°C",
  },
  {
    id: "heavy_rain", label: "Heavy Rainfall", icon: "🌧️", color: "#3B82F6",
    accentColor: "#93C5FD", bgColor: "rgba(59,130,246,0.12)",
    borderColor: "rgba(59,130,246,0.35)",
    gradient: "linear-gradient(135deg,#1D4ED8,#60A5FA)",
    description: "Rain Prob > 60%", heatmapLayer: "rain",
    check: (w) => w.rain !== undefined && w.rain > 60,
    severity: (w) => w.rain > 90 ? "extreme" : w.rain > 75 ? "high" : "moderate",
    metric: (w) => `${Math.round(w.rain)}%`,
    tip: "Areas with >60% precipitation probability",
  },
  {
    id: "high_wind", label: "Strong Winds", icon: "🌬️", color: "#10B981",
    accentColor: "#6EE7B7", bgColor: "rgba(16,185,129,0.12)",
    borderColor: "rgba(16,185,129,0.35)",
    gradient: "linear-gradient(135deg,#047857,#34D399)",
    description: "Wind > 5 km/h", heatmapLayer: "windspeed",
    check: (w) => w.windspeed !== undefined && w.windspeed > 5,
    severity: (w) => w.windspeed > 15 ? "extreme" : w.windspeed > 10 ? "high" : "moderate",
    metric: (w) => `${w.windspeed?.toFixed(1)} km/h`,
    tip: "Areas with wind speed above 5 km/h",
  },
  {
    id: "low_visibility", label: "Low Visibility", icon: "🌫️", color: "var(--od-text-muted)",
    accentColor: "#D1D5DB", bgColor: "rgba(107,114,128,0.12)",
    borderColor: "rgba(107,114,128,0.35)",
    gradient: "linear-gradient(135deg,#4B5563,#9CA3AF)",
    description: "Visibility < 15 km", heatmapLayer: "visibility",
    check: (w) => w.visibility !== undefined && w.visibility < 15,
    severity: (w) => w.visibility < 5 ? "extreme" : w.visibility < 8 ? "high" : "moderate",
    metric: (w) => `${w.visibility?.toFixed(1)} km`,
    tip: "Areas with visibility below 5 km (fog/haze/smoke)",
  },
  {
    id: "snowfall", label: "Snowfall Risk", icon: "🌨️", color: "#8B5CF6",
    accentColor: "#C4B5FD", bgColor: "rgba(139,92,246,0.12)",
    borderColor: "rgba(139,92,246,0.35)",
    gradient: "linear-gradient(135deg,#6D28D9,#A78BFA)",
    description: "Temp ≤ 2°C + Rain>20%", heatmapLayer: "temperature",
    check: (w) => w.temperature !== undefined && w.temperature <= 2 && w.rain > 20,
    severity: (w) => w.temperature < -8 && w.rain > 50 ? "extreme" : w.temperature < 0 && w.rain > 30 ? "high" : "moderate",
    metric: (w) => `${w.temperature?.toFixed(1)}°C | ${Math.round(w.rain)}%`,
    tip: "Areas likely to experience snowfall (low temp + precipitation)",
  },
  {
    id: "thunderstorm", label: "Thunderstorm", icon: "⚡", color: "#F59E0B",
    accentColor: "#FDE68A", bgColor: "rgba(245,158,11,0.12)",
    borderColor: "rgba(245,158,11,0.35)",
    gradient: "linear-gradient(135deg,#B45309,#FCD34D)",
    description: "Rain>50% + Wind>20km/h", heatmapLayer: "rain",
    check: (w) => w.rain > 20 && w.windspeed > 3,
    severity: (w) => w.rain > 60 && w.windspeed > 15 ? "extreme" : w.rain > 40 ? "high" : "moderate",
    metric: (w) => `${Math.round(w.rain)}% | ${w.windspeed?.toFixed(0)} km/h`,
    tip: "Areas at risk of thunderstorms (rain + wind combined)",
  },
  {
    id: "heat_wave", label: "Heat Wave", icon: "🔥", color: "#EF4444",
    accentColor: "#FCA5A5", bgColor: "rgba(239,68,68,0.12)",
    borderColor: "rgba(239,68,68,0.35)",
    gradient: "linear-gradient(135deg,#DC2626,#F97316)",
    description: "Temp ≥ 35°C", heatmapLayer: "temperature",
    check: (w) => w.temperature !== undefined && w.temperature >= 35,
    severity: (w) => w.temperature >= 45 ? "extreme" : w.temperature >= 40 ? "high" : "moderate",
    metric: (w) => ` ${w.temperature?.toFixed(1)}°C | ${Math.round(w.humidity)}% `,
    tip: "Areas with temperature at or above 35°C",
  },
];

// ─── Weather Layers (for strip) ────────────────────────────────────────────────
const WEATHER_LAYERS = [
  { id: "temperature", icon: "🌡️", label: "Temperature", unit: "°C", desc: "Heatwave", gradient: "linear-gradient(135deg,#FF4500,#FFD700)", accentColor: "#FF6B35" },
  { id: "humidity", icon: "💧", label: "Humidity", unit: "%", desc: "Disease & Fog", gradient: "linear-gradient(135deg,#0EA5E9,#BAE6FD)", accentColor: "#0EA5E9" },
  { id: "windspeed", icon: "🌬️", label: "Wind Speed", unit: "km/h", desc: "Cyclone Risk", gradient: "linear-gradient(135deg,#10B981,#A7F3D0)", accentColor: "#10B981" },
  { id: "winddirection", icon: "🧭", label: "Wind Dir.", unit: "°", desc: "Smoke Modeling", gradient: "linear-gradient(135deg,#8B5CF6,#DDD6FE)", accentColor: "#8B5CF6" },
  { id: "rain", icon: "🌧️", label: "Rain Prob.", unit: "%", desc: "Flood Forecast", gradient: "linear-gradient(135deg,#1D4ED8,#93C5FD)", accentColor: "#3B82F6" },
  { id: "thunder", icon: "🌧️", label: "Precip. Prob.", unit: "%", desc: "Rain / Snow Risk", gradient: "linear-gradient(135deg,#1D4ED8,#93C5FD)", accentColor: "#3B82F6" },
  { id: "visibility", icon: "👁️", label: "Visibility", unit: "km", desc: "Transport", gradient: "linear-gradient(135deg,#6B7280,#D1D5DB)", accentColor: "#9CA3AF" },
  { id: "pressure", icon: "📉", label: "Pressure", unit: "hPa", desc: "Storm Form.", gradient: "linear-gradient(135deg,#EC4899,#FBCFE8)", accentColor: "#EC4899" },
  { id: "uv", icon: "☀️", label: "UV Index", unit: "", desc: "Field Exposure", gradient: "linear-gradient(135deg,#EF4444,#FBBF24)", accentColor: "#F97316" },
  { id: "dewpoint", icon: "🌫️", label: "Dew Point", unit: "°C", desc: "Moisture", gradient: "linear-gradient(135deg,#06B6D4,#A5F3FC)", accentColor: "#06B6D4" },
];

// ─── Incident Types ────────────────────────────────────────────────────────────
const INCIDENT_TYPES = {
  FLOOD: { color: "#3B82F6", icon: "💧" }, Flood: { color: "#3B82F6", icon: "💧" },
  COLD_WAVE: { color: "var(--od-text-muted)", icon: "❄️" }, "Cold Wave": { color: "var(--od-text-muted)", icon: "❄️" },
  EARTHQUAKE: { color: "#8B5CF6", icon: "🌍" }, Earthquake: { color: "#8B5CF6", icon: "🌍" },
  FIRE: { color: "#DC2626", icon: "🔥" }, Fire: { color: "#DC2626", icon: "🔥" },
  CYCLONE: { color: "#F59E0B", icon: "🌀" }, Cyclone: { color: "#F59E0B", icon: "🌀" },
  "Heat Wave": { color: "#F97316", icon: "🔥" }, HEAT_WAVE: { color: "#F97316", icon: "🔥" },
  Drought: { color: "#D97706", icon: "🌵" }, DROUGHT: { color: "#D97706", icon: "🌵" },
  Landslide: { color: "#92400E", icon: "⛰️" }, LANDSLIDE: { color: "#92400E", icon: "⛰️" },
  Tsunami: { color: "#0891B2", icon: "🌊" }, TSUNAMI: { color: "#0891B2", icon: "🌊" },
  Avalanche: { color: "#9333EA", icon: "🏔️" }, AVALANCHE: { color: "#9333EA", icon: "🏔️" },
  CLOUDBURST: { color: "#60A5FA", icon: "⛈️" }, "Cloud Burst": { color: "#60A5FA", icon: "⛈️" }, CloudBurst: { color: "#60A5FA", icon: "⛈️" },
  DEFAULT: { color: "#EF4444", icon: "⚠️" },
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const getCardinal = (deg) => ["N", "NE", "E", "SE", "S", "SW", "W", "NW"][Math.round(deg / 45) % 8];

const getSubLabel = (layerId, value) => {
  if (value === null || value === undefined) return null;
  if (layerId === "winddirection") return { label: getCardinal(value), color: "#8B5CF6" };
  if (layerId === "uv") {
    if (value <= 2) return { label: "Low", color: "#10B981" };
    if (value <= 5) return { label: "Moderate", color: "#F59E0B" };
    if (value <= 7) return { label: "High", color: "#F97316" };
    if (value <= 10) return { label: "Very High", color: "#EF4444" };
    return { label: "Extreme", color: "#7C3AED" };
  }
  if (layerId === "visibility") {
    if (value >= 10) return { label: "Clear", color: "#10B981" };
    if (value >= 5) return { label: "Moderate", color: "#F59E0B" };
    if (value >= 2) return { label: "Poor", color: "#F97316" };
    return { label: "Foggy", color: "#EF4444" };
  }
  if (layerId === "temperature") {
    if (value >= 40) return { label: "Extreme", color: "#EF4444" };
    if (value >= 30) return { label: "Hot", color: "#F97316" };
    if (value >= 20) return { label: "Warm", color: "#F59E0B" };
    if (value >= 10) return { label: "Mild", color: "#10B981" };
    return { label: "Cold", color: "#0EA5E9" };
  }
  if (layerId === "humidity") {
    if (value >= 80) return { label: "Very Humid", color: "#EF4444" };
    if (value >= 60) return { label: "Humid", color: "#F59E0B" };
    return { label: "Comfortable", color: "#10B981" };
  }
  if (layerId === "thunder") {
    if (value >= 80) return { label: "Very High", color: "#EF4444" };
    if (value >= 60) return { label: "High", color: "#F97316" };
    if (value >= 30) return { label: "Moderate", color: "#F59E0B" };
    return { label: "Low", color: "#10B981" };
  }
  return null;
};

const formatValue = (layerId, value) => {
  if (value === null || value === undefined) return "—";
  if (layerId === "visibility") return value.toFixed(1);
  if (layerId === "temperature" || layerId === "dewpoint") return value.toFixed(1);
  if (layerId === "windspeed") return value.toFixed(1);
  if (layerId === "pressure") return value.toFixed(0);
  if (layerId === "uv") return value.toFixed(1);
  return Math.round(value);
};

const getFillPercent = (layerId, value) => {
  if (value === null || value === undefined) return 0;
  const ranges = { temperature: [-20, 50], humidity: [0, 100], windspeed: [0, 100], winddirection: [0, 360], rain: [0, 100], thunder: [0, 100], visibility: [0, 20], pressure: [960, 1050], uv: [0, 12], dewpoint: [-20, 35] };
  const [mn, mx] = ranges[layerId] || [0, 100];
  return Math.min(100, Math.max(0, ((value - mn) / (mx - mn)) * 100));
};

// ─── Block centroid + nearest-weather helpers ──────────────────────────────────
const computeCentroid = (geometry) => {
  if (!geometry) return null;
  const lats = [], lngs = [];
  const processRing = (ring) => ring.forEach(([lng, lat]) => { lats.push(lat); lngs.push(lng); });
  if (geometry.type === "Polygon") processRing(geometry.coordinates[0]);
  else if (geometry.type === "MultiPolygon") geometry.coordinates.forEach(poly => processRing(poly[0]));
  if (lats.length === 0) return null;
  return { lat: lats.reduce((a, b) => a + b, 0) / lats.length, lng: lngs.reduce((a, b) => a + b, 0) / lngs.length };
};

const findNearestWeather = (lat, lng, gridData) => {
  if (!gridData || gridData.length === 0) return null;
  let minDist = Infinity, nearest = null;
  for (const pt of gridData) {
    const d = (pt.lat - lat) ** 2 + (pt.lng - lng) ** 2;
    if (d < minDist) { minDist = d; nearest = pt; }
  }
  return nearest?.weather || null;
};

// ─── Point-in-Polygon helpers ──────────────────────────────────────────────────
const _pointInRing = (lat, lng, ring) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

// Caches bounding box on the geometry object to fast-reject points
const getGeometryBounds = (geometry) => {
  if (geometry._bounds) return geometry._bounds;
  let latMin = 90, latMax = -90, lngMin = 180, lngMax = -180;
  const proc = (ring) => {
    for (let i = 0; i < ring.length; i++) {
      const [lng, lat] = ring[i];
      if (lng < lngMin) lngMin = lng;
      if (lng > lngMax) lngMax = lng;
      if (lat < latMin) latMin = lat;
      if (lat > latMax) latMax = lat;
    }
  };
  if (geometry.type === "Polygon") {
    proc(geometry.coordinates[0]);
  } else if (geometry.type === "MultiPolygon") {
    for (let i = 0; i < geometry.coordinates.length; i++) {
      proc(geometry.coordinates[i][0]);
    }
  }
  geometry._bounds = { latMin, latMax, lngMin, lngMax };
  return geometry._bounds;
};

const isPointInGeoJSONGeometry = (lat, lng, geometry) => {
  if (!geometry) return false;

  const bounds = getGeometryBounds(geometry);
  if (lat < bounds.latMin || lat > bounds.latMax || lng < bounds.lngMin || lng > bounds.lngMax) {
    return false;
  }

  if (geometry.type === "Polygon") {
    const [outer, ...holes] = geometry.coordinates;
    if (!_pointInRing(lat, lng, outer)) return false;
    for (const hole of holes) {
      if (_pointInRing(lat, lng, hole)) return false;
    }
    return true;
  }
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.some(poly => {
      const [outer, ...holes] = poly;
      if (!_pointInRing(lat, lng, outer)) return false;
      for (const hole of holes) {
        if (_pointInRing(lat, lng, hole)) return false;
      }
      return true;
    });
  }
  return false;
};

const isPointInGeoJSON = (lat, lng, geojson) => {
  if (!geojson) return false;
  if (geojson.type === "Feature") return isPointInGeoJSONGeometry(lat, lng, geojson.geometry);
  if (geojson.type === "FeatureCollection") return geojson.features.some(f => isPointInGeoJSONGeometry(lat, lng, f.geometry));
  return isPointInGeoJSONGeometry(lat, lng, geojson);
};

// ─── GeoJSON Layer ─────────────────────────────────────────────────────────────
const GeoJSONLayer = ({ map, data, strokeColor, strokeWeight, fillColor, fillOpacity, getPopupContent, getHoverContent, styleFunction }) => {
  const dlRef = useRef(null);
  const iwRef = useRef(null);
  const hoverIwRef = useRef(null);

  useEffect(() => {
    if (!map || !data || !window.google) return;
    const dl = new window.google.maps.Data({ map });
    dlRef.current = dl;
    dl.addGeoJson(data);

    if (styleFunction) {
      dl.setStyle(feature => styleFunction(feature));
    } else {
      dl.setStyle({ strokeColor, strokeWeight, fillColor, fillOpacity, strokeOpacity: 1 });
    }

    let cl = null;
    let hoverInListener = null;
    let hoverOutListener = null;

    if (getPopupContent) {
      const iw = new window.google.maps.InfoWindow();
      iwRef.current = iw;
      cl = dl.addListener("click", e => {
        iw.setContent(getPopupContent(e.feature));
        iw.setPosition(e.latLng);
        iw.open(map);
      });
    }

    if (getHoverContent) {
      const hoverIw = new window.google.maps.InfoWindow({ disableAutoPan: true });
      hoverIwRef.current = hoverIw;
      hoverInListener = dl.addListener("mouseover", e => {
        hoverIw.setContent(getHoverContent(e.feature));
        hoverIw.setPosition(e.latLng);
        hoverIw.open(map);
      });
      hoverOutListener = dl.addListener("mouseout", () => {
        hoverIw.close();
      });
    }

    return () => {
      if (cl) window.google.maps.event.removeListener(cl);
      if (hoverInListener) window.google.maps.event.removeListener(hoverInListener);
      if (hoverOutListener) window.google.maps.event.removeListener(hoverOutListener);
      if (iwRef.current) iwRef.current.close();
      if (hoverIwRef.current) hoverIwRef.current.close();
      dl.setMap(null);
    };
  }, [map, data, strokeColor, strokeWeight, fillColor, fillOpacity, styleFunction]);

  return null;
};

// ─── Canvas Heatmap Overlay ────────────────────────────────────────────────────
class CanvasHeatmapOverlay {
  constructor(map, gridData, layerDef, districtGeoJSON) {
    this.map = map;
    this.gridData = gridData;
    this.layerDef = layerDef;
    this.districtGeoJSON = districtGeoJSON;
    this.canvas = null;
    this.overlay = null;
    this._init();
  }

  _extractRings(geometry) {
    const rings = [];
    if (!geometry) return rings;
    if (geometry.type === "Polygon") {
      geometry.coordinates.forEach(ring =>
        rings.push(ring.map(([lng, lat]) => ({ lat, lng })))
      );
    } else if (geometry.type === "MultiPolygon") {
      geometry.coordinates.forEach(poly =>
        poly.forEach(ring =>
          rings.push(ring.map(([lng, lat]) => ({ lat, lng })))
        )
      );
    }
    return rings;
  }

  _init() {
    const self = this;
    const OverlayClass = class extends window.google.maps.OverlayView {
      onAdd() {
        const canvas = document.createElement("canvas");
        canvas.style.position = "absolute";
        canvas.style.pointerEvents = "none";
        canvas.style.opacity = "0.78";
        self.canvas = canvas;
        this.getPanes().overlayLayer.appendChild(canvas);
      }
      draw() {
        if (!self.canvas) return;
        const proj = this.getProjection();
        if (!proj) return;
        const points = self.gridData.map(pt => {
          const px = proj.fromLatLngToDivPixel(new window.google.maps.LatLng(pt.lat, pt.lng));
          return { x: px.x, y: px.y, value: self.layerDef.valueFn(pt.weather) };
        });
        const allDistrictPixels = [];
        (self.districtGeoJSON || []).forEach(feature => {
          const geom = feature.geometry || feature;
          const rings = self._extractRings(geom);
          rings.forEach(ring => {
            ring.forEach(({ lat, lng }) => {
              const px = proj.fromLatLngToDivPixel(new window.google.maps.LatLng(lat, lng));
              allDistrictPixels.push(px);
            });
          });
        });
        const sourcePixels = allDistrictPixels.length > 0 ? allDistrictPixels : points;
        const xs = sourcePixels.map(p => p.x);
        const ys = sourcePixels.map(p => p.y);
        const PAD = 10;
        const minX = Math.floor(Math.min(...xs)) - PAD;
        const minY = Math.floor(Math.min(...ys)) - PAD;
        const maxX = Math.ceil(Math.max(...xs)) + PAD;
        const maxY = Math.ceil(Math.max(...ys)) + PAD;
        const w = maxX - minX;
        const h = maxY - minY;
        self.canvas.width = w;
        self.canvas.height = h;
        self.canvas.style.left = minX + "px";
        self.canvas.style.top = minY + "px";
        const ctx = self.canvas.getContext("2d");
        ctx.clearRect(0, 0, w, h);
        ctx.save();
        ctx.beginPath();
        (self.districtGeoJSON || []).forEach(feature => {
          const geom = feature.geometry || feature;
          const rings = self._extractRings(geom);
          rings.forEach(ring => {
            ring.forEach(({ lat, lng }, i) => {
              const px = proj.fromLatLngToDivPixel(new window.google.maps.LatLng(lat, lng));
              const lx = px.x - minX;
              const ly = px.y - minY;
              if (i === 0) ctx.moveTo(lx, ly);
              else ctx.lineTo(lx, ly);
            });
            ctx.closePath();
          });
        });
        ctx.clip("evenodd");
        const STEP = 4;
        const POWER = 2.5;
        const imgData = ctx.createImageData(w, h);
        const localPoints = points
          .map(p => ({ x: p.x - minX, y: p.y - minY, value: p.value }))
          .filter(p => p.value !== null && p.value !== undefined);
        for (let py = 0; py < h; py += STEP) {
          for (let px = 0; px < w; px += STEP) {
            let weightSum = 0, valueSum = 0;
            for (const pt of localPoints) {
              const dx = px - pt.x, dy = py - pt.y;
              const dist = Math.sqrt(dx * dx + dy * dy);
              if (dist < 1) { valueSum = pt.value; weightSum = 1; break; }
              const w_ = 1 / Math.pow(dist, POWER);
              weightSum += w_;
              valueSum += w_ * pt.value;
            }
            if (weightSum === 0) continue;
            const interpolated = valueSum / weightSum;
            const colorStr = self.layerDef.colorFn(interpolated);
            const m = colorStr.match(/[\d.]+/g);
            if (!m || m.length < 3) continue;
            const r = parseInt(m[0]);
            const g = parseInt(m[1]);
            const b = parseInt(m[2]);
            const a = Math.round(parseFloat(m[3] ?? 0.85) * 255);
            for (let dy = 0; dy < STEP && py + dy < h; dy++) {
              for (let dx = 0; dx < STEP && px + dx < w; dx++) {
                const idx = ((py + dy) * w + (px + dx)) * 4;
                imgData.data[idx] = r;
                imgData.data[idx + 1] = g;
                imgData.data[idx + 2] = b;
                imgData.data[idx + 3] = a;
              }
            }
          }
        }
        const tmpCanvas = document.createElement("canvas");
        tmpCanvas.width = w;
        tmpCanvas.height = h;
        tmpCanvas.getContext("2d").putImageData(imgData, 0, 0);
        ctx.drawImage(tmpCanvas, 0, 0);
        ctx.restore();
      }
      onRemove() {
        if (self.canvas && self.canvas.parentNode) {
          self.canvas.parentNode.removeChild(self.canvas);
          self.canvas = null;
        }
      }
    };
    this.overlay = new OverlayClass();
    this.overlay.setMap(this.map);
  }

  remove() {
    if (this.overlay) { this.overlay.setMap(null); this.overlay = null; }
  }
  redraw() {
    if (this.overlay) this.overlay.draw();
  }
}

const WeatherHeatmapOverlay = ({ map, gridData, activeHeatmapLayer, districtGeoJSON }) => {
  const overlayRef = useRef(null);
  const layerDef = HEATMAP_LAYERS.find(l => l.id === activeHeatmapLayer) || HEATMAP_LAYERS[0];

  useEffect(() => {
    if (!map || !gridData || gridData.length === 0 || !window.google) return;
    if (!districtGeoJSON || districtGeoJSON.length === 0) return;
    if (overlayRef.current) { overlayRef.current.remove(); overlayRef.current = null; }
    overlayRef.current = new CanvasHeatmapOverlay(map, gridData, layerDef, districtGeoJSON);
    const listener = window.google.maps.event.addListener(map, "idle", () => {
      overlayRef.current?.redraw();
    });
    return () => {
      window.google.maps.event.removeListener(listener);
      if (overlayRef.current) { overlayRef.current.remove(); overlayRef.current = null; }
    };
  }, [map, gridData, activeHeatmapLayer, districtGeoJSON]);

  return null;
};

// ─── Per-layer gradient bar configs ───────────────────────────────────────────
const LAYER_LEGEND_CONFIG = {
  temperature: {
    stops: TEMP_COLOR_STOPS,
    gradientFn: () => TEMP_COLOR_STOPS.map((s, i) => {
      const pct = (i / (TEMP_COLOR_STOPS.length - 1)) * 100;
      return `rgb(${s.color.join(",")}) ${pct}%`;
    }).join(", "),
    labels: ["-25°C", "-10°C", "0°C", "10°C", "25°C", "40°C+"],
  },
  humidity: {
    gradientFn: () =>
      "rgba(191,219,254,0.65) 0%, rgba(96,165,250,0.7) 33%, rgba(59,130,246,0.75) 66%, rgba(30,64,175,0.8) 100%",
    labels: ["0%", "25%", "50%", "75%", "100%"],
  },
  windspeed: {
    gradientFn: () =>
      "rgba(74,222,128,0.65) 0%, rgba(234,179,8,0.7) 33%, rgba(249,115,22,0.8) 66%, rgba(220,38,38,0.85) 100%",
    labels: ["0", "7", "12", "20", "25+ km/h"],
  },
  rain: {
    gradientFn: () =>
      "rgba(219,234,254,0.6) 0%, rgba(96,165,250,0.7) 33%, rgba(37,99,235,0.8) 66%, rgba(30,58,138,0.85) 100%",
    labels: ["0%", "25%", "50%", "75%", "100%"],
  },
  visibility: {
    gradientFn: () =>
      "rgba(127,29,29,0.85) 0%, rgba(220,38,38,0.8) 20%, rgba(251,146,60,0.7) 50%, rgba(134,239,172,0.65) 100%",
    labels: ["<5 km", "8 km", "12 km", "15 km+"],
    reversed: true,
  },
};

// ─── Heatmap Legend ─────────────────────────────────────────────────────────────
const HeatmapLegend = ({ activeLayer, activeCondition, gridData }) => {
  const cond = WEATHER_CONDITIONS.find(c => c.id === activeCondition);
  const cfg = LAYER_LEGEND_CONFIG[activeLayer] || LAYER_LEGEND_CONFIG.temperature;
  const gradientStr = cfg.gradientFn();

  return (
    <div style={{
      position: "absolute", bottom: 14, left: 14, zIndex: 0,
      background: "rgba(15,23,42,0.93)", borderRadius: 10,
      padding: "10px 14px", boxShadow: "0 4px 16px rgba(0,0,0,0.55)",
      border: `1px solid ${cond ? cond.color + "55" : "rgba(255,255,255,0.1)"}`,
      backdropFilter: "blur(8px)", minWidth: 230,
    }}>
      {cond && (
        <div style={{ fontSize: 11, fontWeight: 700, color: cond.color, marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
          <span>{cond.icon}</span>
          <span>{cond.label} — Active</span>
        </div>
      )}
      <div style={{ fontSize: 11, fontWeight: 700, color: "#e2e8f0", marginBottom: 8, display: "flex", alignItems: "center", gap: 6, justifyContent: "space-between" }}>
        <span>
          {HEATMAP_LAYERS.find(l => l.id === activeLayer)?.icon || "🌡️"}
          {" "}{HEATMAP_LAYERS.find(l => l.id === activeLayer)?.label || "Layer"}
          {" "}({HEATMAP_LAYERS.find(l => l.id === activeLayer)?.unit || ""})
        </span>
        {/* <span style={{ fontSize: 10, color: "var(--od-text-muted)", fontWeight: 500 }}>{gridData.length} pts</span> */}
      </div>
      <div style={{ height: 12, borderRadius: 6, overflow: "hidden", background: `linear-gradient(90deg, ${gradientStr})`, border: "1px solid rgba(255,255,255,0.15)", marginBottom: 5, boxShadow: `0 0 8px ${cond ? cond.color + "44" : "rgba(59,130,246,0.3)"}` }} />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "#94a3b8", fontWeight: 600 }}>
        {cfg.labels.map((lbl, i) => <span key={i}>{lbl}</span>)}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3, fontSize: 8, color: "#475569", fontStyle: "italic" }}>
        {activeLayer === "visibility" ? <><span>Dense Fog</span><span>Clear</span></> : <><span>Low</span><span>High</span></>}
      </div>
    </div>
  );
};

// ─── Animated Condition Icons ──────────────────────────────────────────────────
const CONDITION_ICON_STYLES = `
  @keyframes cw-spin   { 0%{transform:rotate(0deg) scale(1)} 50%{transform:rotate(180deg) scale(1.12)} 100%{transform:rotate(360deg) scale(1)} }
  @keyframes cw-crystal{ 0%,100%{opacity:0.7;transform:scale(0.9)} 50%{opacity:1;transform:scale(1.08)} }
  @keyframes hr-drop1  { 0%{transform:translateY(-8px);opacity:0} 60%{opacity:1} 100%{transform:translateY(10px);opacity:0} }
  @keyframes hr-drop2  { 0%{transform:translateY(-8px);opacity:0} 60%{opacity:1} 100%{transform:translateY(10px);opacity:0} }
  @keyframes hr-drop3  { 0%{transform:translateY(-8px);opacity:0} 60%{opacity:1} 100%{transform:translateY(10px);opacity:0} }
  @keyframes hr-splash { 0%{transform:scale(0);opacity:0.8} 100%{transform:scale(1.6);opacity:0} }
  @keyframes sw-wave1  { 0%{transform:translateX(-6px);opacity:0} 50%{opacity:1} 100%{transform:translateX(8px);opacity:0} }
  @keyframes sw-wave2  { 0%{transform:translateX(-6px);opacity:0} 50%{opacity:1} 100%{transform:translateX(8px);opacity:0} }
  @keyframes sw-spin   { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
  @keyframes lv-ring   { 0%{transform:translate(-50%,-50%) scale(0.3);opacity:0.8} 100%{transform:translate(-50%,-50%) scale(1.8);opacity:0} }
  @keyframes lv-pulse  { 0%,100%{opacity:0.5;filter:blur(1px)} 50%{opacity:1;filter:blur(0px)} }
  @keyframes sf-fall1  { 0%{transform:translateY(-6px) rotate(0deg);opacity:0} 20%{opacity:1} 100%{transform:translateY(12px) rotate(180deg);opacity:0} }
  @keyframes sf-fall2  { 0%{transform:translateY(-6px) rotate(45deg);opacity:0} 30%{opacity:1} 100%{transform:translateY(12px) rotate(225deg);opacity:0} }
  @keyframes sf-base   { 0%,100%{transform:rotate(0deg) scale(1)} 25%{transform:rotate(30deg) scale(1.1)} 75%{transform:rotate(-30deg) scale(0.95)} }
  @keyframes ts-flash  { 0%,40%,60%,100%{opacity:0.2} 50%{opacity:1;filter:brightness(2) drop-shadow(0 0 6px #FCD34D)} }
  @keyframes ts-ring   { 0%{transform:translate(-50%,-50%) scale(0.4);opacity:0.9} 100%{transform:translate(-50%,-50%) scale(2);opacity:0} }
  @keyframes ts-bolt   { 0%,100%{transform:scaleY(1)} 50%{transform:scaleY(1.15)} }
`;

const ConditionAnimatedIcon = ({ conditionId, color, size = 36 }) => {
  const s = size;
  switch (conditionId) {
    case "cold_wave":
      return (
        <svg width={s} height={s} viewBox="0 0 36 36" fill="none">
          <g style={{ transformOrigin: "18px 18px" }}>
            {[0, 60, 120, 180, 240, 300].map(deg => (
              <g key={deg} transform={`rotate(${deg} 18 18)`}>
                <line x1="18" y1="0" x2="18" y2="36" stroke={color} strokeWidth="3.5" strokeLinecap="round" />
                <line x1="9" y1="7" x2="18" y2="13" stroke={color} strokeWidth="2.8" strokeLinecap="round" />
                <line x1="27" y1="7" x2="18" y2="13" stroke={color} strokeWidth="2.8" strokeLinecap="round" />
                <line x1="9" y1="29" x2="18" y2="23" stroke={color} strokeWidth="2.8" strokeLinecap="round" />
                <line x1="27" y1="29" x2="18" y2="23" stroke={color} strokeWidth="2.8" strokeLinecap="round" />
              </g>
            ))}
            <circle cx="18" cy="18" r="5.5" fill={color} />
            {[0, 60, 120, 180, 240, 300].map(deg => {
              const rad = (deg - 90) * Math.PI / 180;
              const cx = 18 + Math.cos(rad) * 17;
              const cy = 18 + Math.sin(rad) * 17;
              return <circle key={`tip-${deg}`} cx={cx} cy={cy} r="2.8" fill={color} />;
            })}
          </g>
        </svg>
      );
    case "heavy_rain":
      return (
        <svg width={s} height={s} viewBox="0 0 36 36" fill="none">
          <path d="M8 22 Q6 22 6 18 Q6 14 10 13 Q10 8 15 8 Q19 8 20 11 Q24 10 25 14 Q28 14 28 18 Q28 22 25 22 Z" fill={color} opacity="0.85" />
          {[10, 17, 24].map((x, i) => (
            <ellipse key={i} cx={x} cy="27" rx="1.8" ry="3" fill={color} opacity="0.9"
              style={{ animation: `hr-drop${i + 1} 1.${3 + i}s ease-in ${i * 0.25}s infinite` }} />
          ))}
        </svg>
      );
    case "high_wind":
      return (
        <svg width={s} height={s} viewBox="0 0 36 36" fill="none">
          {[{ y: 9, w: 22, delay: "0s", width: 2.8 }, { y: 17, w: 26, delay: "0.2s", width: 2.4 }, { y: 25, w: 18, delay: "0.4s", width: 2.0 }].map(({ y, w, delay, width }, i) => (
            <g key={i}>
              <path d={`M3 ${y} Q${3 + w * 0.35} ${y - 4} ${3 + w * 0.7} ${y} Q${3 + w * 0.85} ${y + 3} ${3 + w} ${y}`} stroke={color} strokeWidth={width} strokeLinecap="round" fill="none" opacity="0.95" />
              <path d={`M${3 + w} ${y} Q${3 + w + 4} ${y - 3} ${3 + w + 2} ${y + 2}`} stroke={color} strokeWidth={width * 0.7} strokeLinecap="round" fill="none" opacity="0.7" />
            </g>
          ))}
          {[7, 15, 22, 29].map((y, i) => (
            <line key={`dash-${i}`} x1="1" y1={y} x2={5 + (i % 3) * 2} y2={y} stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.45"
              style={{ animation: `sw-wave1 1.4s ease-in-out ${i * 0.15}s infinite` }} />
          ))}
        </svg>
      );
    case "low_visibility":
      return (
        <svg width={s} height={s} viewBox="0 0 36 36" fill="none" overflow="visible">
          <ellipse cx="18" cy="18" rx="13" ry="9" stroke={color} strokeWidth="1.8" fill="none" opacity="0.9" style={{ animation: "lv-pulse 2.5s ease-in-out infinite" }} />
          <circle cx="18" cy="18" r="4" fill={color} opacity="0.7" style={{ animation: "lv-pulse 2.5s ease-in-out 0.5s infinite" }} />
          {[14, 18, 22].map((y, i) => (
            <line key={i} x1="5" y1={y} x2="31" y2={y} stroke="white" strokeWidth="1.4" strokeLinecap="round" opacity={0.25 + i * 0.1} style={{ animation: `lv-pulse 3s ease-in-out ${i * 0.4}s infinite` }} />
          ))}
          {[0, 0.7].map((delay, i) => (
            <circle key={i} cx="18" cy="18" r="12" stroke={color} strokeWidth="1" fill="none" opacity="0" style={{ position: "absolute", animation: `lv-ring 2.5s ease-out ${delay}s infinite`, transformOrigin: "50% 50%" }} />
          ))}
        </svg>
      );
    case "snowfall":
      return (
        <svg width={s} height={s} viewBox="0 0 36 36" fill="none">
          <path d="M7 20 Q5 20 5 17 Q5 13 9 12 Q9 8 14 8 Q18 8 19 11 Q22 10 24 13 Q27 13 27 17 Q27 20 24 20 Z" fill={color} opacity="0.8" />
          <g transform="translate(18 29)" style={{ animation: "sf-base 4s ease-in-out infinite" }}>
            {[0, 60, 120].map(a => (
              <line key={a} transform={`rotate(${a})`} x1="0" y1="-5" x2="0" y2="5" stroke={color} strokeWidth="1.8" strokeLinecap="round" opacity="0.9" />
            ))}
            <circle cx="0" cy="0" r="1.5" fill={color} />
          </g>
          {[10, 24].map((x, i) => (
            <circle key={i} cx={x} cy="26" r="2" fill={color} opacity="0.85" style={{ animation: `sf-fall${i + 1} 1.8s ease-in ${i * 0.5}s infinite` }} />
          ))}
        </svg>
      );
    case "thunderstorm":
      return (
        <svg width={s} height={s} viewBox="0 0 36 36" fill="none">
          <path d="M6 20 Q4 20 4 16 Q4 12 8 11 Q8 6 14 6 Q19 6 20 10 Q24 9 26 13 Q30 13 30 17 Q30 20 26 20 Z" fill={color} opacity="0.75" />
          <path d="M19 20 L14 28 L18 28 L15 36 L24 24 L19 24 L22 20 Z" fill="#FCD34D" stroke="#F59E0B" strokeWidth="0.6"
            style={{ animation: "ts-flash 1.5s ease-in-out infinite, ts-bolt 1.5s ease-in-out infinite" }} />
          {[0, 0.75].map((delay, i) => (
            <circle key={i} cx="18" cy="28" r="7" stroke="#FCD34D" strokeWidth="1" fill="none" opacity="0"
              style={{ animation: `ts-ring 1.5s ease-out ${delay}s infinite` }} />
          ))}
        </svg>
      );
    case "heat_wave":
      return (
        <svg width={s} height={s} viewBox="0 0 36 36" fill="none">
          <circle cx="18" cy="14" r="7" fill={color} opacity="0.9" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map(deg => {
            const rad = (deg - 90) * Math.PI / 180;
            const x1 = 18 + Math.cos(rad) * 9;
            const y1 = 14 + Math.sin(rad) * 9;
            const x2 = 18 + Math.cos(rad) * 13;
            const y2 = 14 + Math.sin(rad) * 13;
            return (
              <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={color} strokeWidth="2.2" strokeLinecap="round" opacity="0.85" />
            );
          })}
          {[12, 18, 24].map((x, i) => (
            <path key={i} d={`M${x} 24 Q${x - 2} 28 ${x} 32 Q${x + 2} 28 ${x} 24`}
              fill={color} opacity={0.6 + i * 0.15}
              style={{ animation: `sf-fall${Math.min(i + 1, 2)} 1.5s ease-in-out ${i * 0.3}s infinite` }} />
          ))}
        </svg>
      );
    default:
      return <span style={{ fontSize: s * 0.7 }}>⚠️</span>;
  }
};

const CONDITION_ICON_BACKGROUNDS = {
  cold_wave: "linear-gradient(135deg, #e38e37f0, #ffb870)",
  heavy_rain: "linear-gradient(135deg, #6375b5f0, #8fa2e0)",
  high_wind: "linear-gradient(135deg, #6375b5f0, #9fb1f0)",
  low_visibility: "linear-gradient(135deg, #374151, #9CA3AF)",
  snowfall: "linear-gradient(135deg, #bf5d93f0, #e79fc4)",
  thunderstorm: "linear-gradient(135deg, #92400E, #FCD34D)",
  heat_wave: "linear-gradient(135deg, #DC2626, #F97316)",
};

// ─── Weather Condition Overlays ────────────────────────────────────────────────
const WeatherConditionOverlays = ({ map, conditionId, hotspots }) => {
  const [openPopupId, setOpenPopupId] = useState(null);
  if (!map || !hotspots || hotspots.length === 0) return null;
  const condition = WEATHER_CONDITIONS.find(c => c.id === conditionId);
  if (!condition) return null;

  const SEVERITY_CONFIG = {
    extreme: { scale: 0.48, glowSpread: 8, glowOpacity: "66", ringCount: 1 },
    high: { scale: 0.48, glowSpread: 6, glowOpacity: "44", ringCount: 1 },
    moderate: { scale: 0.48, glowSpread: 4, glowOpacity: "33", ringCount: 0 },
  };

  const SEVERITY_DOT = { extreme: "#FF2222", high: "#FF8C00", moderate: "#FFD700" };

  return (
    <>
      <style>{CONDITION_ICON_STYLES}</style>
      {hotspots.map((spot, idx) => {
        const isOpen = openPopupId === idx;
        const cfg = SEVERITY_CONFIG[spot.severity] || SEVERITY_CONFIG.moderate;
        return (
          <React.Fragment key={`${conditionId}-${idx}`}>
            <OverlayView position={{ lat: spot.lat, lng: spot.lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
              <div style={{ transform: "translate(-50%,-50%)", pointerEvents: "all", display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer", userSelect: "none" }}
                onClick={e => { e.stopPropagation(); setOpenPopupId(isOpen ? null : idx); }}
                title={`${condition.label} — ${spot.name}`}
              >
                <div
                  style={{ width: Math.round(64 * cfg.scale), height: Math.round(64 * cfg.scale), borderRadius: "50%", background: CONDITION_ICON_BACKGROUNDS[conditionId] || condition.gradient, display: "flex", alignItems: "center", justifyContent: "center", transition: "transform 0.2s cubic-bezier(0.34,1.56,0.64,1)", position: "relative" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.7)"; e.currentTarget.style.zIndex = "999"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.zIndex = "1"; }}
                >
                  <ConditionAnimatedIcon conditionId={conditionId} color="rgba(255,255,255,1)" size={Math.round(40 * cfg.scale)} />
                </div>
              </div>
            </OverlayView>
            {isOpen && (
              <InfoWindow position={{ lat: spot.lat, lng: spot.lng }} onCloseClick={() => setOpenPopupId(null)} options={{ pixelOffset: new window.google.maps.Size(0, -22) }}>
                <div style={{ fontFamily: "system-ui,sans-serif", padding: "6px 8px", minWidth: 220, maxWidth: 300 }}>
                  {/* ── Header: Condition name + severity badge ── */}
                  <div style={{ fontWeight: 700, fontSize: 15, color: condition.color, borderBottom: `2px solid ${condition.color}`, paddingBottom: 6, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 18 }}>{condition.icon}</span>
                    {condition.label}
                    <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 800, color: "#fff", background: SEVERITY_DOT[spot.severity], padding: "3px 9px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.06em" }}>{spot.severity}</span>
                  </div>
 
                  {/* ── Location details ── */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                      <span style={{ fontSize: 16, lineHeight: 1.2, marginTop: 1 }}>📍</span>
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <strong style={{ color: "var(--od-text-primary)", fontSize: 15, lineHeight: 1.2 }}>{spot.name}</strong>
                        {spot.fullAddress && (
                          <span style={{ fontSize: 13, color: "var(--od-text-muted)", lineHeight: 1.4, maxWidth: 230, wordBreak: "break-word" }}>
                            {spot.fullAddress}
                          </span>
                        )}
                      </div>
                    </div>
 
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>🌐</span>
                      <span style={{ fontSize: 13, color: "var(--od-text-muted)", fontFamily: "monospace", lineHeight: 1 }}>
                        {spot.lat.toFixed(5)}°N, {spot.lng.toFixed(5)}°E
                      </span>
                    </div>
                  </div>
 
                  {/* ── Metric row ── */}
                  <div style={{ fontSize: 14, marginBottom: 5, display: "flex", alignItems: "center", gap: 5, fontWeight: 500 }}>
                    <span style={{ fontSize: 16 }}>{conditionId === "cold_wave" ? "🌡️" : conditionId === "heavy_rain" ? "🌧️" : conditionId === "high_wind" ? "💨" : conditionId === "low_visibility" ? "👁️" : conditionId === "snowfall" ? "❄️" : conditionId === "thunderstorm" ? "⚡" : "📊"}</span>
                    <span style={{ color: "var(--od-text-muted)" }}>{conditionId === "cold_wave" ? "Temp / Rain" : conditionId === "heavy_rain" ? "Rain Prob." : conditionId === "high_wind" ? "Wind Speed" : conditionId === "low_visibility" ? "Visibility" : conditionId === "snowfall" ? "Temp / Rain" : conditionId === "thunderstorm" ? "Rain / Wind" : "Temp / Humidity"}</span>
                    <span style={{ color: condition.color, fontWeight: 800, marginLeft: "auto", fontSize: 15 }}>{spot.metric}</span>
                  </div>
 
                  {/* ── Tip / description ── */}
                  <div style={{ fontSize: 12, color: "#000000", fontWeight: 500, marginTop: 6, padding: "4px 8px", borderRadius: 6, background: `${condition.color}10`, border: `1px solid ${condition.color}30` }}>{condition.tip}</div>
                </div>
              </InfoWindow>
            )}
          </React.Fragment>
        );
      })}
      <style>{`@keyframes wcFloat-ring{0%{transform:translate(-50%,-50%) scale(0.4);opacity:0.9}100%{transform:translate(-50%,-50%) scale(2.2);opacity:0}} @keyframes wcFloat-badge{0%,100%{transform:translateY(0px)}50%{transform:translateY(-7px)}}`}</style>
    </>
  );
};

// ─── Service Marker Styles & Component ────────────────────────────────────────
const SERVICE_MARKER_STYLES = `
  @keyframes smPulseRing { 0%{transform:translate(-50%,-50%) scale(0.4);opacity:0.9} 100%{transform:translate(-50%,-50%) scale(2.2);opacity:0} }
  @keyframes smBounce    { 0%,100%{transform:translate(-50%,-50%) translateY(0px) scale(1)} 30%{transform:translate(-50%,-50%) translateY(-6px) scale(1.15)} 60%{transform:translate(-50%,-50%) translateY(-2px) scale(0.97)} }
  @keyframes smGlow      { 0%,100%{filter:drop-shadow(0 2px 4px rgba(0,0,0,0.5)) brightness(1)} 50%{filter:drop-shadow(0 0 10px currentColor) brightness(1.35)} }
  @keyframes smSpin      { 0%{transform:translate(-50%,-50%) rotate(0deg) scale(1)} 25%{transform:translate(-50%,-50%) rotate(-15deg) scale(1.1)} 75%{transform:translate(-50%,-50%) rotate(15deg) scale(1.1)} 100%{transform:translate(-50%,-50%) rotate(0deg) scale(1)} }
  @keyframes smFloatUp   { 0%,100%{transform:translate(-50%,-50%) translateY(0px)} 50%{transform:translate(-50%,-50%) translateY(-5px)} }
  @keyframes smPingDot   { 0%{transform:scale(0.8);opacity:1} 70%,100%{transform:scale(2.4);opacity:0} }
`;

const ServiceMarker = ({ place, color, accentColor, markerEmoji, label, isOpen, onClick, onClose, parentId }) => {
  const cfg = CATEGORY_ANIM_CONFIG[parentId] || DEFAULT_ANIM_CONFIG;
  const animDelay = `${((place.lat * 1000 + place.lng * 100) % 18) * 0.1}s`;

  return (
    <>
      <style>{SERVICE_MARKER_STYLES}</style>
      <OverlayView position={{ lat: place.lat, lng: place.lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
        <div onClick={e => { e.stopPropagation(); onClick(); }} title={place.name}
          style={{ position: "relative", width: cfg.size + 16, height: cfg.size + 16, cursor: "pointer", transform: "translate(-50%,-50%)", display: "flex", alignItems: "center", justifyContent: "center" }}>

          <div
            style={{ position: "relative", width: cfg.size, height: cfg.size, borderRadius: "50%", background: cfg.bgColor, border: cfg.border, backdropFilter: "blur(2px)", boxShadow: [`0 0 0 3px rgba(255,255,255,0.9)`, `0 4px 16px ${cfg.shadowColor}`, `0 0 24px ${cfg.pulseColor}55`].join(", "), display: "flex", alignItems: "center", justifyContent: "center", fontSize: cfg.size * 0.62, lineHeight: 1, transition: "transform 0.15s ease, box-shadow 0.15s ease", zIndex: 2, userSelect: "none", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.6))" }}
            onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.4)"; e.currentTarget.style.boxShadow = [`0 0 0 3px rgba(255,255,255,1)`, `0 6px 24px ${cfg.shadowColor}`, `0 0 32px ${cfg.pulseColor}88`].join(", "); }}
            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = [`0 0 0 3px rgba(255,255,255,0.9)`, `0 4px 16px ${cfg.shadowColor}`, `0 0 24px ${cfg.pulseColor}55`].join(", "); }}
          >
            {markerEmoji}
          </div>
        </div>
      </OverlayView>
      {isOpen && (
        <InfoWindow position={{ lat: place.lat, lng: place.lng }} onCloseClick={onClose} options={{ pixelOffset: new window.google.maps.Size(0, -(cfg.size / 2 + 18)) }}>
          <div style={{ minWidth: 210, maxWidth: 280, fontFamily: "sans-serif" }}>
            <h6 style={{ marginBottom: 6, color, borderBottom: `2px solid ${color}`, paddingBottom: 4, fontWeight: "bold", display: "flex", alignItems: "center", gap: 5, fontSize: 13 }}>
              {markerEmoji} {label}
            </h6>
            <div style={{ fontSize: 12, marginBottom: 4 }}><strong>{place.name}</strong></div>
            {place.vicinity && <div style={{ fontSize: 11, color: "#555", marginBottom: 3 }}>📍 {place.vicinity}</div>}
            {place.openNow !== undefined && (
              <div style={{ fontSize: 11, marginTop: 4, color: place.openNow ? "#10B981" : "#EF4444", fontWeight: 600 }}>
                {place.openNow ? "● Open Now" : "● Closed"}
              </div>
            )}
          </div>
        </InfoWindow>
      )}
    </>
  );
};

// ─── Incident Marker ──────────────────────────────────────────────────────────
const IncidentMarker = ({ marker, isOpen, onClick, onClose }) => {
  const config = INCIDENT_TYPES[marker.category] || INCIDENT_TYPES.DEFAULT;
  return (
    <>
      <OverlayView position={{ lat: marker.position[0], lng: marker.position[1] }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
        <div onClick={e => { e.stopPropagation(); onClick(); }} title={marker.name}
          style={{ position: "relative", width: 44, height: 44, cursor: "pointer", transform: "translate(-22px,-22px)" }}>
          {[0, 1].map(i => (
            <div key={i} style={{ position: "absolute", borderRadius: "50%", background: `${config.color}22`, border: `1.5px solid ${config.color}55`, top: "50%", left: "50%", width: 44 - i * 8, height: 44 - i * 8, transform: "translate(-50%,-50%)", animation: `dmpulse 2s ease-out ${i * 0.4}s infinite` }} />
          ))}
          <div style={{ position: "absolute", width: 24, height: 24, borderRadius: "50%", background: config.color, border: "2px solid white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, top: "50%", left: "50%", transform: "translate(-50%,-50%)", boxShadow: `0 2px 8px ${config.color}88`, zIndex: 10 }}>
            {config.icon}
          </div>
          <style>{`@keyframes dmpulse{0%{transform:translate(-50%,-50%) scale(0.8);opacity:0.9}100%{transform:translate(-50%,-50%) scale(1.6);opacity:0}}`}</style>
        </div>
      </OverlayView>
      {isOpen && (
        <InfoWindow position={{ lat: marker.position[0], lng: marker.position[1] }} onCloseClick={onClose} options={{ pixelOffset: new window.google.maps.Size(0, -30) }}>
          <div style={{ minWidth: 220, maxWidth: 280, fontFamily: "sans-serif" }}>
            <h6 style={{ marginBottom: 8, color: config.color, borderBottom: `2px solid ${config.color}`, paddingBottom: 4, fontWeight: "bold", display: "flex", alignItems: "center", gap: 5, fontSize: 13 }}>{config.icon} {marker.category || "Incident"}</h6>
            <div style={{ fontSize: 12, marginBottom: 5 }}><strong>Name:</strong> {marker.name || "NA"}</div>
            {marker.address && <div style={{ fontSize: 11, color: "#555" }}><strong>Location:</strong> {marker.address}</div>}
            {marker.description && <div style={{ fontSize: 11, color: "#555", marginTop: 4 }}><strong>Desc:</strong> {marker.description}</div>}
          </div>
        </InfoWindow>
      )}
    </>
  );
};

// ─── Layer Switcher ────────────────────────────────────────────────────────────
const LayerSwitcher = ({ activeLayer, onChange, isOpen, onToggle }) => {
  const layers = [
    { id: "terrain", name: "Terrain", icon: "⛰️" },
    { id: "road", name: "Road", icon: "🗺️" },
    { id: "satellite", name: "Satellite", icon: "🛰️" },
    { id: "hybrid", name: "Hybrid", icon: "🌐" },
  ];
  // const MAP_TYPE_IDS = { road: "roadmap", satellite: "satellite", terrain: "terrain", hybrid: "hybrid" };
  return (
    <div style={{ position: "absolute", top: 5, right: 0, zIndex: 0 }}>
      <button onClick={onToggle} title="Layers" style={{ width: 36, height: 36, background: "var(--od-card-bg)", border: "1px solid rgba(0,0,0,0.15)", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="var(--od-text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M2 12L12 17L22 12" stroke="var(--od-text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M2 17L12 22L22 17" stroke="var(--od-text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {isOpen && (
        <div style={{ position: "absolute", top: 1, right: 38, background: "var(--od-card-bg)", borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.4)", overflow: "hidden", minWidth: 150, border: "1px solid rgba(255,255,255,0.1)" }}>
          {/* <div style={{ padding: "7px 12px", borderBottom: "1px solid rgba(255,255,255,0.08)", fontWeight: 600, color: "#94a3b8", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>Base Layer</div> */}
          {layers.map(l => (
            <div key={l.id} onClick={() => onChange(l.id)} style={{ padding: "8px 12px", cursor: "pointer", background: activeLayer === l.id ? "rgba(59,130,246,0.15)" : "transparent", display: "flex", alignItems: "center", gap: 8, color: activeLayer === l.id ? "#000000" : "#000000", fontSize: 12, fontWeight: activeLayer === l.id ? 600 : 400 }}>
              {/* <span>{l.icon}</span>{l.name} */}
              {l.name}
              {activeLayer === l.id && <span style={{ marginLeft: "auto", fontSize: 10, color: "#60a5fa" }}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Zoom Control ──────────────────────────────────────────────────────────────
const ZoomControl = ({ map }) => {
  const handleZoom = (delta) => {
    if (!map) return;
    map.setZoom(Math.min(20, Math.max(4, (map.getZoom() || 7) + delta)));
  };
  return (
    <div style={{ position: "absolute", top: 279, bottom: 0, right: 0, zIndex: 10, display: "flex", flexDirection: "column" }}>
      {[{ label: "+", delta: 1 }, { label: "−", delta: -1 }].map(({ label, delta }) => (
        <button key={label} onClick={() => handleZoom(delta)}
          style={{ width: 37, height: 37, background: "var(--od-card-bg-translucent)", border: "1px solid var(--od-border)", cursor: "pointer", fontSize: 18, fontWeight: 700, color: "var(--od-text-secondary)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.15)", lineHeight: 1, transition: "background 0.15s" }}
          onMouseEnter={e => e.currentTarget.style.background = "var(--od-surface-alt)"}
          onMouseLeave={e => e.currentTarget.style.background = "var(--od-card-bg-translucent)"}
        >{label}</button>
      ))}
    </div>
  );
};

// ─── Incident Badge ────────────────────────────────────────────────────────────
const IncidentBadge = ({ count, colorMap }) => (
  <div style={{ position: "absolute", bottom: 10, right: 10, zIndex: 0, background: "rgba(10,22,44,0.92)", borderRadius: 10, padding: "7px 10px", boxShadow: "0 2px 12px rgba(0,0,0,0.4)", fontSize: 10, minWidth: 120, border: "1px solid rgba(255,255,255,0.08)" }}>
    <div style={{ fontWeight: 700, color: "#f0f9ff", marginBottom: 5, fontSize: 11 }}>📍 {count} Incidents</div>
    {Object.entries(colorMap).slice(0, 4).map(([cat, color]) => (
      <div key={cat} style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
        <span style={{ color: "#94a3b8", whiteSpace: "nowrap", fontSize: 10 }}>{INCIDENT_TYPES[cat]?.icon || "⚠️"} {cat}</span>
      </div>
    ))}
  </div>
);

// ─── Map Loading Overlay ───────────────────────────────────────────────────────
const MapLoadingOverlay = () => (
  <div style={{ position: "absolute", inset: 0, zIndex: 0, background: "rgba(10,22,44,0.75)", backdropFilter: "blur(3px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
    <div style={{ position: "relative", width: 60, height: 60 }}>
      {[0, 1, 2].map(i => <div key={i} style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "2px solid #3B82F6", opacity: 0, animation: `dmRadar 1.8s ease-out ${i * 0.6}s infinite` }} />)}
      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 24, height: 24, borderRadius: "50%", background: "linear-gradient(135deg,#3B82F6,#6366F1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>🗺️</div>
    </div>
    <div style={{ color: "#E2E8F0", fontSize: 12, fontWeight: 600, letterSpacing: "0.06em" }}>Loading Map…</div>
    <style>{`@keyframes dmRadar{0%{transform:scale(0.3);opacity:0.9}100%{transform:scale(1.8);opacity:0}}`}</style>
  </div>
);

// ─── Horizontal Weather Strip ──────────────────────────────────────────────────
const WeatherStrip = ({ weatherData, loading, lastUpdated, allStates, statesLoading, currentStateId, currentStateName, onStateChange, capitalName }) => {
  const scrollRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scroll = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 220, behavior: "smooth" });
  };

  // Only enable each arrow while there's actually more strip to scroll to —
  // re-checked on every scroll (smooth-scroll fires many scroll events, so
  // this also gives live feedback that a click landed) and whenever the
  // strip's content or the window size changes.
  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 1);
  };

  useEffect(() => {
    updateScrollButtons();
    window.addEventListener("resize", updateScrollButtons);
    return () => window.removeEventListener("resize", updateScrollButtons);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weatherData, loading]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filteredStates = useMemo(() => {
    if (!allStates || allStates.length === 0) return [];
    const q = searchTerm.toLowerCase().trim();
    if (!q) return allStates;
    return allStates.filter(s => (s.name || "").toLowerCase().includes(q));
  }, [allStates, searchTerm]);

  return (
    <div style={{ width: "100%", background: "var(--od-surface-alt)", borderBottom: "1px solid var(--od-border)", flexShrink: 0, display: "flex", alignItems: "center", padding: "0 10px", height: 52, gap: 0, overflow: "visible" }}>

      {/* ── State Selector ── */}
      <div ref={dropdownRef} style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 12, borderRight: "1px solid var(--od-border)", marginRight: 10, flexShrink: 0, position: "relative" }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: "linear-gradient(135deg, #fbbf24, #f59e0b)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 2px 6px rgba(245,158,11,0.3)" }}>
          <span style={{ fontSize: 17, lineHeight: 1 }}>🌤️</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "var(--od-text-muted)", whiteSpace: "nowrap", letterSpacing: "0.06em", textTransform: "uppercase", lineHeight: 1 }}>
            Select State for Weather
          </div>
          <div
            onClick={() => setDropdownOpen(prev => !prev)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "4px 8px 4px 10px",
              background: dropdownOpen ? "#eff6ff" : "var(--od-card-bg)",
              border: dropdownOpen ? "1.5px solid #3b82f6" : "1.5px solid var(--od-border)",
              borderRadius: 7,
              cursor: "pointer",
              userSelect: "none",
              transition: "all 0.15s ease",
              boxShadow: dropdownOpen ? "0 0 0 3px rgba(59,130,246,0.12)" : "0 1px 2px rgba(0,0,0,0.05)",
              minWidth: 130,
            }}
            onMouseEnter={e => { if (!dropdownOpen) { e.currentTarget.style.borderColor = "#93c5fd"; e.currentTarget.style.background = "var(--od-surface-alt)"; } }}
            onMouseLeave={e => { if (!dropdownOpen) { e.currentTarget.style.borderColor = "var(--od-border)"; e.currentTarget.style.background = "var(--od-card-bg)"; } }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--od-text-primary)", whiteSpace: "nowrap", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis" }}>
                {currentStateName || "Select State"}
              </div>
              {capitalName && (
                <div style={{ fontSize: 9, color: "var(--od-text-muted)", whiteSpace: "nowrap", fontWeight: 500, lineHeight: 1.3 }}>
                  📍 {capitalName}
                </div>
              )}
            </div>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0, transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease", opacity: 0.5 }}>
              <path d="M2 3.5L5 6.5L8 3.5" stroke="var(--od-text-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        {/* ── Dropdown ── */}
        {dropdownOpen && (
          <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, width: 220, background: "var(--od-card-bg)", borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.18)", border: "1px solid var(--od-border)", zIndex: 1, overflow: "hidden", animation: "wsDropIn 0.15s ease-out" }}>

            {/* Search */}
            <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--od-border)", background: "var(--od-surface-alt)" }}>
              <input
                type="text" placeholder="Search state…" value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)} autoFocus
                style={{ width: "100%", padding: "6px 8px", border: "1px solid var(--od-border)", borderRadius: 6, fontSize: 11, outline: "none", background: "var(--od-card-bg)", color: "var(--od-text-secondary)", boxSizing: "border-box" }}
                onFocus={e => e.target.style.borderColor = "#3b82f6"}
                onBlur={e => e.target.style.borderColor = "var(--od-border)"}
              />
            </div>

            {/* List */}
            <div style={{ maxHeight: 220, overflowY: "auto", padding: "4px 6px", scrollbarWidth: "thin" }}>
              {statesLoading ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 0", justifyContent: "center", color: "var(--od-text-muted)", fontSize: 11 }}>
                  <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid #3b82f6", borderTopColor: "transparent", animation: "wsSpin 0.7s linear infinite" }} />
                  Loading…
                </div>
              ) : filteredStates.length === 0 ? (
                <div style={{ padding: "14px 0", textAlign: "center", fontSize: 11, color: "var(--od-text-muted)" }}>
                  {searchTerm ? `No match for "${searchTerm}"` : "No states available"}
                </div>
              ) : (
                filteredStates.map(state => {
                  const isActive = state.id === currentStateId;
                  const capital = getCapitalForState(state.name);
                  return (
                    <div
                      key={state.id}
                      onClick={() => { onStateChange(state.id, state.name); setDropdownOpen(false); setSearchTerm(""); }}
                      style={{
                        padding: "7px 10px", borderRadius: 6, cursor: "pointer", marginBottom: 2,
                        display: "flex", alignItems: "center", gap: 8,
                        background: isActive ? "rgba(59,130,246,0.1)" : "transparent",
                        border: isActive ? "1px solid rgba(59,130,246,0.3)" : "1px solid transparent",
                        transition: "background 0.12s",
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "var(--od-surface-alt)"; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = isActive ? "rgba(59,130,246,0.1)" : "transparent"; }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 11.5, fontWeight: isActive ? 700 : 500, color: isActive ? "#1d4ed8" : "var(--od-text-secondary)", display: "block" }}>
                          {state.name}
                        </span>
                        {/* {capital && (
                          <span style={{ fontSize: 9.5, color: "var(--od-text-muted)", fontWeight: 400 }}>
                            Capital: {capital.name}
                          </span>
                        )} */}
                      </div>
                      {isActive && <span style={{ fontSize: 10, color: "#3b82f6" }}>✓</span>}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Weather cards (unchanged logic) ── */}
      <button
        onClick={() => scroll(-1)}
        disabled={!canScrollLeft}
        title={canScrollLeft ? "Scroll left" : ""}
        style={{
          flexShrink: 0, width: 22, height: 34,
          border: "1px solid var(--od-border)",
          background: "var(--od-surface-alt)",
          borderRadius: 6,
          cursor: canScrollLeft ? "pointer" : "not-allowed",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginRight: 4, color: "var(--od-text-secondary)", fontSize: 13,
          opacity: canScrollLeft ? 1 : 0.35,
          transition: "background 0.1s, transform 0.1s",
        }}
        onMouseDown={e => { if (canScrollLeft) e.currentTarget.style.transform = "scale(0.9)"; }}
        onMouseUp={e => { e.currentTarget.style.transform = "scale(1)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
      >◀</button>
      <div
        ref={scrollRef}
        onScroll={updateScrollButtons}
        style={{ flex: 1, display: "flex", overflowX: "auto", gap: 5, alignItems: "center", height: "100%", scrollbarWidth: "none", padding: "5px 0" }}
      >
        {WEATHER_LAYERS.map(layer => {
          const raw = weatherData[layer.id];
          const value = formatValue(layer.id, raw);
          const sub = getSubLabel(layer.id, raw);
          const fill = getFillPercent(layer.id, raw);

          let displayDesc = layer.desc;
          if (layer.id === "temperature" && raw !== null && raw !== undefined) {
            displayDesc = raw <= 15 ? "Cold Wave" : "Heat Wave";
          }

          return (
            <div key={layer.id} style={{ flexShrink: 0, height: 40, minWidth: 108, background: "var(--od-card-bg)", border: "1px solid var(--od-border)", borderRadius: 8, padding: "0 10px", display: "flex", alignItems: "center", gap: 7, position: "relative", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 2, background: layer.gradient, borderRadius: "8px 0 0 8px" }} />
              <span style={{ fontSize: 14, flexShrink: 0, marginLeft: 2 }}>{layer.icon}</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 0, minWidth: 0 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: "var(--od-text-primary)", lineHeight: 1.2, whiteSpace: "nowrap" }}>{layer.label}</span>
                <span style={{ fontSize: 9, color: "var(--od-text-muted)", whiteSpace: "nowrap" }}>{displayDesc}</span>
              </div>
              <div style={{ width: 1, height: 22, background: "var(--od-border)", flexShrink: 0 }} />
              <div style={{ display: "flex", alignItems: "baseline", gap: 2, flexShrink: 0 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: loading ? "var(--od-text-muted)" : layer.accentColor, lineHeight: 1 }}>{loading ? "—" : value}</span>
                {!loading && raw !== null && raw !== undefined && layer.unit && <span style={{ fontSize: 9, color: "var(--od-text-muted)" }}>{layer.unit}</span>}
              </div>
              {!loading && sub && <span style={{ fontSize: 8.5, fontWeight: 700, color: sub.color, background: `${sub.color}18`, border: `1px solid ${sub.color}35`, padding: "1px 5px", borderRadius: 20, flexShrink: 0, whiteSpace: "nowrap" }}>{sub.label}</span>}
              {!loading && raw !== null && raw !== undefined && (
                <div style={{ position: "absolute", bottom: 0, left: 2, right: 0, height: 2, background: "var(--od-surface-alt)", borderRadius: 1, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${fill}%`, background: layer.gradient, transition: "width 1.2s ease" }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button
        onClick={() => scroll(1)}
        disabled={!canScrollRight}
        title={canScrollRight ? "Scroll right" : ""}
        style={{
          flexShrink: 0, width: 22, height: 34,
          border: "1px solid var(--od-border)",
          background: "var(--od-surface-alt)",
          borderRadius: 6,
          cursor: canScrollRight ? "pointer" : "not-allowed",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginLeft: 4, color: "var(--od-text-secondary)", fontSize: 13,
          opacity: canScrollRight ? 1 : 0.35,
          transition: "background 0.1s, transform 0.1s",
        }}
        onMouseDown={e => { if (canScrollRight) e.currentTarget.style.transform = "scale(0.9)"; }}
        onMouseUp={e => { e.currentTarget.style.transform = "scale(1)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
      >▶</button>

      <style>{`
        @keyframes wsSpin { to { transform: rotate(360deg) } }
        @keyframes wsDropIn { 0%{opacity:0;transform:translateY(-6px) scale(0.97)} 100%{opacity:1;transform:translateY(0) scale(1)} }
      `}</style>
    </div>
  );
};

// ─── GeoJSON bounds util ───────────────────────────────────────────────────────
const getGeoJSONBounds = (geojson) => {
  if (!geojson || !window.google) return null;
  const bounds = new window.google.maps.LatLngBounds();
  const proc = (c) => { if (!Array.isArray(c)) return; if (typeof c[0] === "number") bounds.extend({ lat: c[1], lng: c[0] }); else c.forEach(proc); };
  const geom = geojson.geometry || geojson;
  if (geom.coordinates) proc(geom.coordinates);
  return bounds;
};

// ─── Weather fetchers ──────────────────────────────────────────────────────────
const fetchGridPointWeather = async (lat, lng) => {
  const cacheKey = `${lat},${lng}`;
  if (_weatherCache.has(cacheKey)) return _weatherCache.get(cacheKey);
  try {
    const res = await fetch(
      `https://weather.googleapis.com/v1/currentConditions:lookup?key=${GOOGLE_MAPS_API_KEY}&location.latitude=${lat}&location.longitude=${lng}`
    );
    if (res.status === 429) return null;
    const json = await res.json();
    if (!json || json.error) return null;
    const result = {
      temperature: json.temperature?.degrees ?? null,
      humidity: json.relativeHumidity ?? null,
      windspeed: json.wind?.speed?.value ?? null,
      winddirection: json.wind?.direction?.degrees ?? null,
      rain: json.precipitation?.probability?.percent ?? null,
      pressure: json.airPressure?.meanSeaLevelMillibars ?? null,
      visibility: json.visibility?.distance ?? null,
      uv: json.uvIndex ?? null,
      dewpoint: json.dewPoint?.degrees ?? null,
    };
    _weatherCache.set(cacheKey, result);
    return result;
  } catch { return null; }
};

const fetchBulkWeather = async (points) => {
  if (_gridWeatherCache) return _gridWeatherCache;
  const CONCURRENCY = 8, DELAY_MS = 100;
  const results = new Array(points.length).fill(null);
  const sleep = (ms) => new Promise(r => setTimeout(r, ms));
  let i = 0;
  const runSlot = async () => {
    while (i < points.length) {
      const idx = i++;
      results[idx] = await fetchGridPointWeather(points[idx].lat, points[idx].lng);
      await sleep(DELAY_MS);
    }
  };
  await Promise.all(Array.from({ length: CONCURRENCY }, () => runSlot()));
  _gridWeatherCache = results;
  return results;
};

// ─── Geocoding helpers ─────────────────────────────────────────────────────────
const _geocodeCache = new Map();
 
const getPlaceName = async (lat, lng) => {
  const key = `${lat.toFixed(3)},${lng.toFixed(3)}`;
  if (_geocodeCache.has(key)) return _geocodeCache.get(key);
 
  // ── 1. Standard reverse-geocode ───────────────────────────────────────
  try {
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
    );
    const data = await res.json();
 
    if (data.status === "OK" && data.results?.length > 0) {
      const priority = [
        "locality",
        "sublocality_level_1",
        "sublocality",
        "neighborhood",
        "administrative_area_level_3",
        "administrative_area_level_2",
        "natural_feature",
        "colloquial_area",
        "administrative_area_level_1",
      ];
 
      for (const type of priority) {
        for (const result of data.results) {
          const comp = result.address_components?.find(
            (c) =>
              c.types.includes(type) &&
              c.long_name &&
              !c.long_name.includes("+") &&
              c.long_name.length > 1 &&
              !/^\d+$/.test(c.long_name) &&
              !/^unnamed/i.test(c.long_name)
          );
          if (comp) {
            _geocodeCache.set(key, comp.long_name);
            return comp.long_name;
          }
        }
      }
 
      // Try formatted_address
      for (const result of data.results) {
        const formatted = result.formatted_address || "";
        const stripped = formatted
          .replace(/^[A-Z0-9]{4,8}\+[A-Z0-9]{2,3}\s*/i, "")
          .trim();
        const parts = stripped
          .split(",")
          .map((p) => p.trim())
          .filter(
            (p) =>
              p.length > 1 &&
              !/^\d+$/.test(p) &&
              !p.includes("+") &&
              !/^unnamed/i.test(p)
          );
        if (parts.length > 0) {
          const name = parts.slice(0, 2).join(", ");
          _geocodeCache.set(key, name);
          return name;
        }
      }
    }
  } catch (e) {
    console.error("Geocode error:", e);
  }
 
  // ── 2. Google Places Nearby Search (10 km) ────────────────────────────
  try {
    const nearbyRes = await fetch(
      `https://maps.googleapis.com/maps/api/place/nearbysearch/json?location=${lat},${lng}&radius=10000&key=${GOOGLE_MAPS_API_KEY}`
    );
    const nearbyData = await nearbyRes.json();
    if (nearbyData.status === "OK" && nearbyData.results?.length > 0) {
      const preferred = nearbyData.results.find(
        (r) =>
          r.name &&
          !r.types?.includes("route") &&
          !/^unnamed/i.test(r.name) &&
          !r.name.includes("+")
      );
      if (preferred) {
        let name = preferred.name;
        if (preferred.vicinity && preferred.vicinity !== preferred.name) {
          const vicinityParts = preferred.vicinity
            .split(",")
            .map((p) => p.trim())
            .filter(
              (p) =>
                p.length > 1 &&
                p !== preferred.name &&
                !/^\d+$/.test(p) &&
                !/^unnamed/i.test(p)
            );
          if (vicinityParts.length > 0) {
            name = `Near ${preferred.name}, ${vicinityParts[0]}`;
          }
        }
        _geocodeCache.set(key, name);
        return name;
      }
    }
  } catch (e) {
    console.error("Nearby Places fallback error:", e);
  }
 
  // ── 3. Admin-area-only reverse geocode ────────────────────────────────
  try {
    const adminRes = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&result_type=administrative_area_level_2|administrative_area_level_1&key=${GOOGLE_MAPS_API_KEY}`
    );
    const adminData = await adminRes.json();
    if (adminData.status === "OK" && adminData.results?.length > 0) {
      const comps = adminData.results[0].address_components || [];
      const district = comps.find((c) =>
        c.types.includes("administrative_area_level_2")
      );
      const state = comps.find((c) =>
        c.types.includes("administrative_area_level_1")
      );
      if (district || state) {
        const name = [district?.long_name, state?.long_name]
          .filter(Boolean)
          .join(", ");
        _geocodeCache.set(key, name);
        return name;
      }
    }
  } catch (e) {
    console.error("Admin-area fallback error:", e);
  }
 
  // ── 4. Coordinates fallback (no hardcoded city) ───────────────────────
  const fallback = `${Math.abs(lat).toFixed(4)}°${lat >= 0 ? "N" : "S"}, ${Math.abs(lng).toFixed(4)}°${lng >= 0 ? "E" : "W"}`;
  _geocodeCache.set(key, fallback);
  return fallback;
};
 

const _fullAddressCache = new Map();

const _adminHierarchyCache = new Map();
const getAdminHierarchy = async (lat, lng) => {
  const key = `adm-${lat.toFixed(3)},${lng.toFixed(3)}`;
  if (_adminHierarchyCache.has(key)) return _adminHierarchyCache.get(key);
 
  const result = { locality: "", district: "", state: "", postal: "", country: "" };
  try {
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
    );
    const data = await res.json();
    if (data.status === "OK" && data.results?.length > 0) {
      // Scan ALL results for components — the first result may not have them all
      for (const r of data.results) {
        for (const comp of (r.address_components || [])) {
          if (!result.locality && (comp.types.includes("locality") || comp.types.includes("administrative_area_level_3"))) {
            const val = comp.long_name;
            if (val && val.length > 1 && !/^\d+$/.test(val) && !val.includes("+") && !/^unnamed/i.test(val)) {
              result.locality = val;
            }
          }
          if (!result.district && comp.types.includes("administrative_area_level_2")) {
            result.district = comp.long_name || "";
          }
          if (!result.state && comp.types.includes("administrative_area_level_1")) {
            result.state = comp.long_name || "";
          }
          if (!result.postal && comp.types.includes("postal_code")) {
            result.postal = comp.long_name || "";
          }
          if (!result.country && comp.types.includes("country")) {
            result.country = comp.long_name || "";
          }
        }
        // Stop early if we have everything
        if (result.district && result.state) break;
      }
    }
  } catch (e) {
    console.error("Admin hierarchy fetch error:", e);
  }
  _adminHierarchyCache.set(key, result);
  return result;
};
 
/**
 * Enrich a short address string with the admin hierarchy.
 * E.g. "Durbuk" → "Durbuk, Leh, Ladakh, 194401"
 */
const enrichAddress = (shortAddr, hierarchy) => {
  if (!shortAddr) return "";
  const existing = shortAddr.toLowerCase();
  const parts = [shortAddr];
 
  // Append each admin level if not already present in the string
  if (hierarchy.locality && !existing.includes(hierarchy.locality.toLowerCase()) && hierarchy.locality !== shortAddr) {
    parts.push(hierarchy.locality);
  }
  if (hierarchy.district && !existing.includes(hierarchy.district.toLowerCase())) {
    parts.push(hierarchy.district);
  }
  if (hierarchy.state && !existing.includes(hierarchy.state.toLowerCase())) {
    parts.push(hierarchy.state);
  }
  if (hierarchy.postal && !existing.includes(hierarchy.postal)) {
    parts.push(hierarchy.postal);
  }
 
  return parts.join(", ");
};
 
const getFullAddress = async (lat, lng) => {
  const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
  if (_fullAddressCache.has(key)) return _fullAddressCache.get(key);
 
  /**
   * Clean a formatted_address string:
   * strips plus-codes, "Unnamed Road", leading commas, lone PIN codes.
   */
  const cleanAddress = (raw) => {
    if (!raw) return "";
    let s = raw
      .replace(/^[A-Z0-9]{4,8}\+[A-Z0-9]{2,3}\s*/i, "")
      .trim()
      .replace(/^,\s*/, "");
    s = s.replace(/^Unnamed\s+\w+\s*,\s*/gi, "");
    if (/^\d{5,6}$/.test(s.trim())) return "";
    return s;
  };
 
  // Always pre-fetch the admin hierarchy — we'll use it to enrich short results
  const hierarchy = await getAdminHierarchy(lat, lng);
 
  // ── 1. Standard reverse geocode ───────────────────────────────────────
  try {
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
    );
    const data = await res.json();
 
    if (data.status === "OK" && data.results?.length > 0) {
 
      // Pass A — formatted_address
      for (const candidate of data.results) {
        if (candidate.types?.includes("plus_code")) continue;
 
        const formatted = cleanAddress(candidate.formatted_address);
        if (!formatted) continue;
 
        const parts = formatted.split(",").map((p) => p.trim());
        const meaningfulParts = parts.filter(
          (p) =>
            p.length > 1 &&
            !/^\d{5,6}$/.test(p) &&
            !/^unnamed/i.test(p) &&
            !p.includes("+")
        );
 
        if (meaningfulParts.length >= 2) {
          // Already has multiple parts — good enough, but still enrich if missing district/state
          const address = enrichAddress(meaningfulParts.join(", "), hierarchy);
          _fullAddressCache.set(key, address);
          return address;
        }
      }
 
      // Pass B — build from address_components
      for (const candidate of data.results) {
        if (candidate.types?.includes("plus_code")) continue;
        const comps = candidate.address_components || [];
        const addressParts = [];
        const typeOrder = [
          "premise",
          "route",
          "sublocality_level_2",
          "sublocality_level_1",
          "sublocality",
          "locality",
          "administrative_area_level_3",
          "administrative_area_level_2",
          "administrative_area_level_1",
        ];
        const seen = new Set();
        for (const type of typeOrder) {
          const c = comps.find(
            (comp) =>
              comp.types.includes(type) &&
              comp.long_name &&
              !seen.has(comp.long_name) &&
              !/^\d+$/.test(comp.long_name) &&
              !comp.long_name.includes("+") &&
              !/^unnamed/i.test(comp.long_name)
          );
          if (c) {
            addressParts.push(c.long_name);
            seen.add(c.long_name);
          }
        }
        const postal = comps.find((c) => c.types.includes("postal_code"));
        if (postal && addressParts.length > 0) addressParts.push(postal.long_name);
 
        if (addressParts.length >= 2) {
          const address = addressParts.join(", ");
          _fullAddressCache.set(key, address);
          return address;
        }
      }
    }
  } catch (e) {
    console.error("Full address geocode error:", e);
  }
 
  // ── 2. Nearby Places fallback + enrichment ────────────────────────────
  try {
    const nearbyRes = await fetch(
      `https://maps.googleapis.com/maps/api/place/nearbysearch/json?location=${lat},${lng}&radius=15000&key=${GOOGLE_MAPS_API_KEY}`
    );
    const nearbyData = await nearbyRes.json();
    if (nearbyData.status === "OK" && nearbyData.results?.length > 0) {
      const best = nearbyData.results.find(
        (r) =>
          r.vicinity &&
          r.vicinity.length > 3 &&
          !/^unnamed/i.test(r.name || "") &&
          !(r.name || "").includes("+")
      );
      if (best) {
        let shortAddr = best.vicinity || best.name;
        if (best.name && !best.vicinity.includes(best.name)) {
          shortAddr = `Near ${best.name}, ${best.vicinity}`;
        }
        // KEY FIX: Enrich the short result with district/state/postal
        const address = enrichAddress(shortAddr, hierarchy);
        _fullAddressCache.set(key, address);
        return address;
      }
    }
  } catch (e) {
    console.error("Nearby Places address fallback error:", e);
  }
 
  // ── 3. Build from admin hierarchy directly ────────────────────────────
  {
    const parts = [];
    if (hierarchy.locality) parts.push(hierarchy.locality);
    if (hierarchy.district) parts.push(hierarchy.district);
    if (hierarchy.state) parts.push(hierarchy.state);
    if (hierarchy.postal) parts.push(hierarchy.postal);
    if (parts.length >= 2) {
      const address = parts.join(", ");
      _fullAddressCache.set(key, address);
      return address;
    }
  }
 
  // ── 4. Use getPlaceName + enrichment as last resort ───────────────────
  const placeName = await getPlaceName(lat, lng);
  const address = enrichAddress(placeName, hierarchy);
  _fullAddressCache.set(key, address);
  return address;
};

// ─── Generate condition hotspots ───────────────────────────────────────────────
export const generateConditionHotspots = async (conditionId, heatmapGridData, districtGeoJSON, stateGeoJSON, regionGeoJSON) => {
  const condition = WEATHER_CONDITIONS.find(c => c.id === conditionId);
  if (!condition || !heatmapGridData.length) return [];

  const clipFeatures =
    Array.isArray(regionGeoJSON) && regionGeoJSON.length > 0
      ? regionGeoJSON
      : stateGeoJSON ? [stateGeoJSON] : null;

  const validPoints = heatmapGridData.filter(pt => {
    if (!pt.weather) return false;
    if (clipFeatures) {
      const inRegion = clipFeatures.some(feature => isPointInGeoJSON(pt.lat, pt.lng, feature));
      if (!inRegion) return false;
    }
    return true;
  });

  const getLayerValue = (weather) => {
    switch (condition.heatmapLayer) {
      case "temperature": return weather.temperature;
      case "humidity": return weather.humidity;
      case "windspeed": return weather.windspeed;
      case "rain": return weather.rain;
      case "visibility": return weather.visibility;
      default: return null;
    }
  };

  const values = validPoints.map(pt => getLayerValue(pt.weather)).filter(v => v !== null && v !== undefined);
  if (values.length === 0) return [];
  const average = values.reduce((sum, v) => sum + v, 0) / values.length;

  const matchingPoints = validPoints.filter(pt => {
    if (!condition.check(pt.weather)) return false;
    const value = getLayerValue(pt.weather);
    return value !== null && value !== undefined;
  });
  if (matchingPoints.length === 0) return [];

  const CELL = 1.2;
  const cellMap = new Map();
  matchingPoints.forEach(pt => {
    const value = getLayerValue(pt.weather);
    const cellKey = `${Math.floor(pt.lat / CELL)}_${Math.floor(pt.lng / CELL)}`;
    const existing = cellMap.get(cellKey);
    const sev = condition.severity(pt.weather);
    const sevRank = { extreme: 3, high: 2, moderate: 1 }[sev] || 0;
    const isBetter = !existing || (condition.heatmapLayer === "visibility" ? value < existing.value : sevRank > existing.sevRank || (sevRank === existing.sevRank && value > existing.value));
    if (isBetter) cellMap.set(cellKey, { ...pt, sev, sevRank, value });
  });

  const MAX_HOTSPOTS = 8;
  const topEntries = Array.from(cellMap.values())
    .sort((a, b) => { if (b.sevRank !== a.sevRank) return b.sevRank - a.sevRank; return condition.heatmapLayer === "visibility" ? a.value - b.value : b.value - a.value; })
    .slice(0, MAX_HOTSPOTS);

  const [names, fullAddresses] = await Promise.all([
    Promise.all(topEntries.map(pt => getPlaceName(pt.lat, pt.lng))),
    Promise.all(topEntries.map(pt => getFullAddress(pt.lat, pt.lng))),
  ]);

  const usedNames = new Set();
  const hotspots = [];
  topEntries.forEach((pt, i) => {
    const name = names[i];
    if (usedNames.has(name)) return;
    usedNames.add(name);
    hotspots.push({
      lat: pt.lat, lng: pt.lng, name, fullAddress: fullAddresses[i],
      metric: condition.metric(pt.weather), severity: pt.sev, inAlert: true,
      aboveAvgBy: condition.heatmapLayer === "visibility" ? (average - pt.value).toFixed(1) : (pt.value - average).toFixed(1),
    });
  });
  return hotspots;
};

// ─── Type-safe gis_id comparison ──────────────────────────────────────────────
const gisIdMatch = (a, b) => {
  if (a == null || b == null) return false;
  return String(a) === String(b);
};

// ─── DashboardMap ──────────────────────────────────────────────────────────────
const DashboardMap = ({ height = "480px", gisMap }) => {
  // ── Dynamic profile state ──────────────────────────────
  const [stateId, setStateId] = useState(null);
  const [stateName, setStateName] = useState("");
  const [stateCenter, setStateCenter] = useState(DEFAULT_CENTER);
  const [districtIds, setDistrictIds] = useState([]);
  const [blockIds, setBlockIds] = useState([]);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [allStates, setAllStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);

  // ── Capital city state (for weather strip) ──────────────
  const [capitalCenter, setCapitalCenter] = useState(null);
  const [capitalName, setCapitalName] = useState("");

  const [mapInstance, setMapInstance] = useState(null);
  const [stateGeoJSON, setStateGeoJSON] = useState(null);
  const [districtGeoJSON, setDistrictGeoJSON] = useState([]);
  const [incidentMarkers, setIncidentMarkers] = useState([]);
  const [openPopupId, setOpenPopupId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeBaseLayer, setActiveBaseLayer] = useState("terrain");
  const [weatherData, setWeatherData] = useState({});
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [enabledConditions, setEnabledConditions] = useState([]);
  const [blockGeoJSON, setBlockGeoJSON] = useState([]);
  const stateBoundsRef = useRef(null);

  // ── Essential Services state (lifted here, passed to EssentialServicesPanel) ──
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [enabledSubcategories, setEnabledSubcategories] = useState([]);
  const [subcategoryMarkers, setSubcategoryMarkers] = useState({});
  const [loadingSubcategories, setLoadingSubcategories] = useState([]);
  const [openServicePopupId, setOpenServicePopupId] = useState(null);

  const [activeHeatmapCondition, setActiveHeatmapCondition] = useState(null);
  const [heatmapGridData, setHeatmapGridData] = useState([]);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [weatherAlertsOpen, setWeatherAlertsOpen] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const [regionDropdownOptions, setRegionDropdownOptions] = useState([]);
  const [regionDropdownLoading, setRegionDropdownLoading] = useState(false);
  const [selectedHeatmapRegions, setSelectedHeatmapRegions] = useState(new Set());
  const [heatmapRegionGeoJSON, setHeatmapRegionGeoJSON] = useState(null);
  const [conditionHotspots, setConditionHotspots] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapContainerRef = useRef(null);

  const MAP_TYPE_IDS = { road: "roadmap", satellite: "satellite", terrain: "terrain", hybrid: "hybrid" };

  const activeHeatmapLayer = useMemo(() => {
    if (!activeHeatmapCondition) return null;
    const cond = WEATHER_CONDITIONS.find(c => c.id === activeHeatmapCondition);
    return cond?.heatmapLayer || "temperature";
  }, [activeHeatmapCondition]);

  const { isLoaded: mapsLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const mapOptions = useMemo(() => ({
    mapTypeId: MAP_TYPE_IDS[activeBaseLayer] || "terrain",
    minZoom: 4, scrollwheel: true, disableDefaultUI: false,
    mapTypeControl: false, streetViewControl: false, fullscreenControl: false, zoomControl: false,
  }), [activeBaseLayer]);

  const onMapLoad = useCallback(map => {
    setMapInstance(map);
    window._dashboardMapRef = map;
  }, []);
  const onMapUnmount = useCallback(() => setMapInstance(null), []);

  // ── Fullscreen support ─────────────────────────────────
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      mapContainerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  const loadHeatmapData = useCallback(async () => {
    if (heatmapGridData.length > 0) return heatmapGridData;
    if (!stateGeoJSON) return [];

    const gridPoints = generateGridPoints(stateGeoJSON);
    if (gridPoints.length === 0) return [];

    setHeatmapLoading(true);
    try {
      const weatherResults = await fetchBulkWeather(gridPoints);
      const gridData = gridPoints.map((pt, idx) => ({
        ...pt,
        weather: weatherResults[idx] || { temperature: null, humidity: null, windspeed: null, winddirection: null, rain: null, pressure: null, visibility: null, uv: null, dewpoint: null },
      })).filter(pt => pt.weather.temperature !== null);
      setHeatmapGridData(gridData);
      return gridData;
    } catch (err) {
      console.error("Failed to load heatmap data:", err);
      return [];
    } finally {
      setHeatmapLoading(false);
    }
  }, [heatmapGridData, stateGeoJSON]);

  useEffect(() => {
    if (stateGeoJSON && activeHeatmapCondition && heatmapGridData.length === 0) {
      loadHeatmapData();
    }
  }, [stateGeoJSON, activeHeatmapCondition]);



  const buildDistrictStyleFn = useCallback(() => ({
    strokeColor: "#FF8C00", strokeWeight: 1.5, strokeOpacity: 1, fillColor: "transparent", fillOpacity: 0,
  }), []);

  const handleToggleCondition = useCallback((conditionId) => {
    const isEnabled = enabledConditions.includes(conditionId);
    if (isEnabled) { setEnabledConditions([]); setActiveHeatmapCondition(null); return; }
    setEnabledConditions([conditionId]);
    setActiveHeatmapCondition(conditionId);
    loadHeatmapData();
  }, [enabledConditions, loadHeatmapData]);

  const handleStateChange = useCallback((newStateId, newStateName) => {
    if (newStateId === stateId) return;

    // Reset all map data for the new state
    setStateGeoJSON(null);
    setDistrictGeoJSON([]);
    setBlockGeoJSON([]);
    setIncidentMarkers([]);
    setHeatmapGridData([]);
    setConditionHotspots([]);
    setHeatmapRegionGeoJSON(null);
    setRegionDropdownOptions([]);
    setSelectedHeatmapRegions(new Set());
    setSubcategoryMarkers({});
    setEnabledSubcategories([]);
    setExpandedCategories([]);

    // Clear weather caches so new state gets fresh data
    _weatherCache.clear();
    _gridWeatherCache = null;
    setWeatherData({});
    setWeatherLoading(true);

    // Look up capital for the new state and update weather source
    const capital = getCapitalForState(newStateName);
    if (capital) {
      setCapitalCenter({ lat: capital.lat, lng: capital.lng });
      setCapitalName(capital.name);
    } else {
      setCapitalCenter(null);
      setCapitalName("");
    }

    // Set new state
    setStateId(newStateId);
    setStateName(newStateName);
  }, [stateId]);

  // ── Essential Services handlers (passed as props to EssentialServicesPanel) ──
  const handleToggleCategory = useCallback((catId) => {
    setExpandedCategories(prev =>
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  }, []);

  const handleToggleSubcategory = useCallback(async (subId, parentCat) => {
    const isEnabled = enabledSubcategories.includes(subId);
    if (isEnabled) {
      setEnabledSubcategories(prev => prev.filter(id => id !== subId));
      setSubcategoryMarkers(prev => { const n = { ...prev }; delete n[subId]; return n; });
    } else {
      setEnabledSubcategories(prev => [...prev, subId]);
      if (mapInstance) {
        setLoadingSubcategories(prev => [...prev, subId]);
        const sub = parentCat.subcategories.find(s => s.id === subId);
        if (sub) {
          const isFullState = selectedHeatmapRegions.size === 1 && [...selectedHeatmapRegions][0] === `state-${stateId}`;
          const clipBoundary =
            !isFullState && Array.isArray(heatmapRegionGeoJSON) && heatmapRegionGeoJSON.length > 0
              ? { type: "FeatureCollection", features: heatmapRegionGeoJSON }
              : stateGeoJSON;
          const places = await fetchPlacesAcrossState(mapInstance, sub, clipBoundary);
          setSubcategoryMarkers(prev => ({ ...prev, [subId]: places }));
        }
        setLoadingSubcategories(prev => prev.filter(id => id !== subId));
      }
    }
  }, [enabledSubcategories, mapInstance, stateGeoJSON, heatmapRegionGeoJSON, selectedHeatmapRegions]);

  /**
   * Fetch weather data for the weather strip.
   * Uses capitalCenter (capital city coords) if available, otherwise falls back to stateCenter.
   */
  const fetchWeather = async (center) => {
    const coords = center || capitalCenter || stateCenter;
    setWeatherLoading(true);
    try {
      const res = await fetch(
        `https://weather.googleapis.com/v1/currentConditions:lookup?key=${GOOGLE_MAPS_API_KEY}&location.latitude=${coords.lat}&location.longitude=${coords.lng}`
      );
      const json = await res.json();
      if (!json || json.error) throw new Error(json?.error?.message || "Weather fetch failed");
      const parsedWeather = {
        temperature: json.temperature?.degrees ?? null,
        humidity: json.relativeHumidity ?? null,
        windspeed: json.wind?.speed?.value ?? null,
        winddirection: json.wind?.direction?.degrees ?? null,
        rain: json.precipitation?.probability?.percent ?? null,
        thunder: json.thunderstormProbability ?? json.precipitation?.probability?.percent ?? null,
        pressure: json.airPressure?.meanSeaLevelMillibars ?? null,
        visibility: json.visibility?.distance ?? null,
        uv: json.uvIndex ?? null,
        dewpoint: json.dewPoint?.degrees ?? null,
      };
      console.log(`[Weather] ${stateName} (${capitalName || "center"})`, parsedWeather);
      setWeatherData(parsedWeather);
      setLastUpdated(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }));
    } catch (e) { console.error("[Weather] Fetch failed:", e); }
    setWeatherLoading(false);
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`, { headers: { "User-Agent": "IncidentManagementSystem/1.0" } });
      const data = await res.json();
      if (data?.address) { const a = data.address; const p = []; if (a.village || a.town || a.city) p.push(a.village || a.town || a.city); if (a.county) p.push(a.county); if (a.state) p.push(a.state); return p.join(", ") || data.display_name; }
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    } catch { return `${lat.toFixed(4)}, ${lng.toFixed(4)}`; }
  };

  const loadStateBoundary = async () => {
    if (!stateId) return;
    try {
      const res = await getData(`${getStateRegions}${stateId}/regions`);
      if (!res) return;

      // ── 1. Parse STATE boundary ────────────────────────────────────────────
      const stateData = res.state;
      if (stateData) {
        let stateCoords;
        try {
          stateCoords = typeof stateData.coordinates === "string"
            ? JSON.parse(stateData.coordinates)
            : stateData.coordinates;
        } catch (e) {
          console.error("Invalid state coordinates JSON:", e);
          stateCoords = null;
        }

        if (stateCoords) {
          const stateFeature = {
            type: "Feature",
            properties: {
              name: stateData.name,
              region_id: stateData.region_id,
              gis_id: stateData.region_id,
              type: "state",
            },
            geometry: stateCoords,
          };
          setStateGeoJSON(stateFeature);
          setHeatmapRegionGeoJSON([stateFeature]);

          // Pan map to state bounds
          if (window.google) {
            const bounds = getGeoJSONBounds(stateFeature);
            if (bounds && !bounds.isEmpty()) {
              const center = bounds.getCenter();
              setStateCenter({ lat: center.lat(), lng: center.lng() });
              if (mapInstance) mapInstance.fitBounds(bounds, { top: 30, right: 30, bottom: 30, left: 30 });
            }
          }
        }
      }

      // ── 2. Parse DISTRICT boundaries ──────────────────────────────────────
      const districtsData = Array.isArray(res.districts) ? res.districts : [];
      const districtFeatures = districtsData
        .map(d => {
          let coords;
          try {
            coords = typeof d.coordinates === "string" ? JSON.parse(d.coordinates) : d.coordinates;
          } catch (e) {
            console.warn(`Invalid district coordinates for ${d.name}:`, e);
            return null;
          }
          if (!coords) return null;
          return {
            type: "Feature",
            properties: {
              name: d.name,
              region_id: d.region_id,
              gis_id: d.region_id,
              parent_region_id: d.parent_region_id,
              type: "district",
            },
            geometry: coords,
          };
        })
        .filter(Boolean);
      setDistrictGeoJSON(districtFeatures);

      // Extract district IDs for downstream use
      setDistrictIds(districtsData.map(d => d.region_id).filter(Boolean));

      // ── 3. Parse BLOCK boundaries ─────────────────────────────────────────
      const blocksData = Array.isArray(res.blocks) ? res.blocks : [];
      const blockFeatures = blocksData
        .map(b => {
          let coords;
          try {
            coords = typeof b.coordinates === "string" ? JSON.parse(b.coordinates) : b.coordinates;
          } catch (e) {
            console.warn(`Invalid block coordinates for ${b.name}:`, e);
            return null;
          }
          if (!coords) return null;
          return {
            type: "Feature",
            properties: {
              name: b.name,
              region_id: b.region_id,
              gis_id: b.region_id,
              parent_region_id: b.parent_region_id,
              type: "block",
            },
            geometry: coords,
          };
        })
        .filter(Boolean);
      setBlockGeoJSON(blockFeatures);

      // Extract block IDs
      setBlockIds(blocksData.map(b => b.region_id).filter(Boolean));

      // ── 4. Weather: use capital city or state centroid ────────────────────
      const capital = getCapitalForState(stateName);
      if (capital) {
        const capitalCoords = { lat: capital.lat, lng: capital.lng };
        setCapitalCenter(capitalCoords);
        setCapitalName(capital.name);
        fetchWeather(capitalCoords);
      } else if (window.google && stateData) {
        const tmpFeature = { type: "Feature", geometry: typeof stateData.coordinates === "string" ? JSON.parse(stateData.coordinates) : stateData.coordinates };
        const bounds = getGeoJSONBounds(tmpFeature);
        if (bounds && !bounds.isEmpty()) {
          const center = bounds.getCenter();
          fetchWeather({ lat: center.lat(), lng: center.lng() });
        }
      }

    } catch (e) {
      console.error("loadStateBoundary error:", e);
    }
  };



  const loadIncidents = async () => {
    try {
      const data = await getData(getincidentcatcreate.replace("STATEID", stateId));
      if (data && Array.isArray(data) && data.length > 0) {
        const markers = (await Promise.all(data.map(async inc => {
          try {
            if (inc.gis_config) { const g = JSON.parse(inc.gis_config); if (g.incidentcenter && Array.isArray(g.incidentcenter)) { const [lng, lat] = g.incidentcenter; const address = await reverseGeocode(lat, lng); return { id: inc.id, position: [lat, lng], name: inc.name, description: inc.description, category: inc.category, created_on: inc.created_on, address }; } }
            return null;
          } catch { return null; }
        }))).filter(Boolean);
        setIncidentMarkers(markers);
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (stateId) {
      setSelectedHeatmapRegions(new Set());
    }
  }, [stateId]);

  // ─── Fetch stateId from profile API (like RegionTree) ───────────────
  useEffect(() => {
    const getUserProfile = async () => {
      try {
        const profile = await getData(getThemeURL);
        if (profile && profile.length > 0) {
          const sid = profile[0].state_id;
          const sname = profile[0].statename || "STATE";
          setStateId(sid);
          setStateName(sname);

          // Look up capital for the profile state
          const capital = getCapitalForState(sname);
          if (capital) {
            setCapitalCenter({ lat: capital.lat, lng: capital.lng });
            setCapitalName(capital.name);
          }

          // If the profile has lat/lng for the state, use them for map center
          if (profile[0].latitude && profile[0].longitude) {
            setStateCenter({
              lat: parseFloat(profile[0].latitude),
              lng: parseFloat(profile[0].longitude),
            });
          }
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      } finally {
        setProfileLoaded(true);
      }
    };
    getUserProfile();
  }, []);

  // ─── Fetch all states for the dropdown ────────────────────────────────
  useEffect(() => {
    const fetchStates = async () => {
      setStatesLoading(true);
      try {
        const res = await getData(getAllStates);
        if (Array.isArray(res)) setAllStates(res);
      } catch (e) {
        console.error("Error fetching states:", e);
      } finally {
        setStatesLoading(false);
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    if (!stateId) return;

    const init = async () => {
      setIsLoading(true);
      await Promise.all([
        loadStateBoundary(),
        // loadIncidents(),
      ]);
      setIsLoading(false);
    };
    init();
    // Refresh weather every 10 minutes using capital coords
    const iv = setInterval(() => fetchWeather(), 10 * 60 * 1000);
    return () => clearInterval(iv);
  }, [stateId]);

  useEffect(() => {
    if (stateGeoJSON && mapInstance && window.google)
      stateBoundsRef.current = getGeoJSONBounds(stateGeoJSON);
  }, [stateGeoJSON, mapInstance]);

  useEffect(() => {
    if (!stateId) return;
    const loadRegionOptions = async () => {
      setRegionDropdownLoading(true);
      try { setRegionDropdownOptions(await buildRegionDropdownOptions(stateId)); }
      catch (e) { console.error("Failed to build region dropdown:", e); }
      finally { setRegionDropdownLoading(false); }
    };
    loadRegionOptions();
  }, [stateId]);

  const handleHeatmapRegionSelect = useCallback(async (nextSet, newFeatures, nowChecked, node) => {
    setSelectedHeatmapRegions(nextSet);
    if (!nextSet || nextSet.size === 0) {
      setHeatmapRegionGeoJSON(stateGeoJSON ? [stateGeoJSON] : null);
      // Zoom back to full state
      if (mapInstance && window.google && stateBoundsRef.current) {
        mapInstance.fitBounds(stateBoundsRef.current, { top: 30, right: 30, bottom: 30, left: 30 });
      }
      return;
    }
    if (nowChecked && newFeatures && newFeatures.length > 0) {
      const selectedType = node?.type;
      setHeatmapRegionGeoJSON((prev) => {
        const existing = Array.isArray(prev) ? prev : [];
        if (selectedType === "district") {
          const childBlockGisIds = new Set((node?.children || []).map(c => String(c.gis_id)));
          const filtered = existing.filter(f => f?.properties?.type !== "state" && !(f?.properties?.type === "district" && gisIdMatch(f?.properties?.gis_id, node?.gis_id)) && !(f?.properties?.type === "block" && childBlockGisIds.has(String(f?.properties?.gis_id))));
          return [...filtered, ...newFeatures];
        } else if (selectedType === "block") {
          const parentGisId = node?.parentGisId ?? null;
          const filtered = existing.filter(f => f?.properties?.type !== "state" && !(f?.properties?.type === "district" && gisIdMatch(f?.properties?.gis_id, parentGisId)) && !gisIdMatch(f?.properties?.gis_id, node?.gis_id));
          return [...filtered, ...newFeatures];
        }
        return [...newFeatures];
      });
      if (mapInstance && window.google) {
        if (selectedType === "state" && stateBoundsRef.current) {
          mapInstance.fitBounds(stateBoundsRef.current, { top: 30, right: 30, bottom: 30, left: 30 });
        } else if (newFeatures[0]) {
          const bounds = new window.google.maps.LatLngBounds();
          const processCoords = (coords) => { if (!Array.isArray(coords)) return; if (typeof coords[0] === "number") bounds.extend({ lat: coords[1], lng: coords[0] }); else coords.forEach(processCoords); };
          const geom = newFeatures[0].geometry;
          if (geom?.coordinates) processCoords(geom.coordinates);
          if (!bounds.isEmpty()) mapInstance.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
        }
      }
    } else if (!nowChecked) {
      const idsToRemove = new Set();
      if (node?.gis_id != null) idsToRemove.add(String(node.gis_id));
      if (node?.type === "block" && node?.parentGisId != null) idsToRemove.add(String(node.parentGisId));
      if (node?.type === "district" && node?.children) node.children.forEach(c => { if (c.gis_id != null) idsToRemove.add(String(c.gis_id)); });
      setHeatmapRegionGeoJSON((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) { if (newFeatures && newFeatures.length > 0) return newFeatures; return stateGeoJSON ? [stateGeoJSON] : null; }
        const filtered = node?.type === "state" ? [] : prev.filter(f => !idsToRemove.has(String(f?.properties?.gis_id ?? "")));
        const existingIds = new Set(filtered.map(f => String(f?.properties?.gis_id ?? "")));
        const uniqueNew = (newFeatures || []).filter(f => { const fid = String(f?.properties?.gis_id ?? ""); if (existingIds.has(fid)) return false; existingIds.add(fid); return true; });
        const combined = [...filtered, ...uniqueNew];
        return combined.length === 0 ? (stateGeoJSON ? [stateGeoJSON] : null) : combined;
      });

      // Zoom out: fit to remaining features or back to full state
      if (mapInstance && window.google) {
        const remaining = newFeatures && newFeatures.length > 0 ? newFeatures : null;
        if (remaining && remaining.some(f => f?.properties?.type !== "state")) {
          // Fit to the remaining selected features
          const bounds = new window.google.maps.LatLngBounds();
          const processCoords = (coords) => { if (!Array.isArray(coords)) return; if (typeof coords[0] === "number") bounds.extend({ lat: coords[1], lng: coords[0] }); else coords.forEach(processCoords); };
          remaining.forEach(f => { if (f?.geometry?.coordinates) processCoords(f.geometry.coordinates); });
          if (!bounds.isEmpty()) mapInstance.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
        } else if (stateBoundsRef.current) {
          // No specific region remaining — zoom back to full state
          mapInstance.fitBounds(stateBoundsRef.current, { top: 30, right: 30, bottom: 30, left: 30 });
        }
      }
    }
  }, [mapInstance, stateGeoJSON]);

  useEffect(() => {
    if (!activeHeatmapCondition || heatmapGridData.length === 0) { setConditionHotspots([]); return; }
    let cancelled = false;
    generateConditionHotspots(activeHeatmapCondition, heatmapGridData, districtGeoJSON, stateGeoJSON, heatmapRegionGeoJSON)
      .then(spots => { if (!cancelled) setConditionHotspots(spots); });
    return () => { cancelled = true; };
  }, [activeHeatmapCondition, heatmapGridData, districtGeoJSON, stateGeoJSON, heatmapRegionGeoJSON]);

  // Re-fetch service markers when region changes
  useEffect(() => {
    if (enabledSubcategories.length === 0 || !mapInstance) return;
    const isFullState = selectedHeatmapRegions.size === 1 && [...selectedHeatmapRegions][0] === `state-${stateId}`;
    const clipBoundary = !isFullState && Array.isArray(heatmapRegionGeoJSON) && heatmapRegionGeoJSON.length > 0
      ? { type: "FeatureCollection", features: heatmapRegionGeoJSON } : stateGeoJSON;

    let cancelled = false;

    // Immediately clear markers so old region's markers don't linger while fetching
    setSubcategoryMarkers({});

    const refetch = async () => {
      for (const subId of enabledSubcategories) {
        if (cancelled) break;
        const parentCat = SERVICE_CATEGORIES.find(cat => cat.subcategories.some(s => s.id === subId));
        if (!parentCat) continue;
        const sub = parentCat.subcategories.find(s => s.id === subId);
        if (!sub) continue;

        setLoadingSubcategories(prev => [...prev, subId]);
        const places = await fetchPlacesAcrossState(mapInstance, sub, clipBoundary);

        if (!cancelled) {
          setSubcategoryMarkers(prev => ({ ...prev, [subId]: places }));
        }
        setLoadingSubcategories(prev => prev.filter(id => id !== subId));
      }
    };
    refetch();

    return () => {
      cancelled = true;
    };
  }, [heatmapRegionGeoJSON, stateGeoJSON]);

  const categoryColorMap = useMemo(() => {
    const m = {};
    incidentMarkers.forEach(mk => { if (mk.category && !m[mk.category]) m[mk.category] = INCIDENT_TYPES[mk.category]?.color || "#EF4444"; });
    return m;
  }, [incidentMarkers]);

  const allServiceMarkerEntries = useMemo(() => {
    const entries = [];
    enabledSubcategories.forEach(subId => {
      const subDef = ALL_SUBCATEGORIES[subId];
      if (!subDef) return;
      const places = subcategoryMarkers[subId] || [];
      places.forEach(place => {
        entries.push({ key: `${subId}-${place.id}`, place, color: subDef.parentColor, accentColor: subDef.parentAccent, markerEmoji: subDef.markerEmoji, label: subDef.label, parentId: subDef.parentId });
      });
    });
    return entries;
  }, [enabledSubcategories, subcategoryMarkers]);

  const subcategoryCounts = useMemo(() => {
    const c = {};
    Object.entries(subcategoryMarkers).forEach(([id, places]) => { c[id] = places.length; });
    return c;
  }, [subcategoryMarkers]);

  const showHeatmap = activeHeatmapCondition !== null && heatmapGridData.length > 0;

  return (
    <div ref={mapContainerRef} style={{ width: "100%", borderRadius: isFullscreen ? 0 : 10, overflow: "hidden", marginBottom: 19, border: "none", background: "var(--od-surface-alt)", top: 10, position: "relative" }}>
      <div style={{ position: "relative", width: "100%", height: isFullscreen ? "100vh" : height, display: "flex", flexDirection: "column", background: "#0f121a" }}>
        {!gisMap && <WeatherStrip weatherData={weatherData} loading={weatherLoading} lastUpdated={lastUpdated}
          allStates={allStates}
          statesLoading={statesLoading}
          currentStateId={stateId}
          currentStateName={stateName}
          onStateChange={handleStateChange}
          capitalName={capitalName}
        />}

        <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
          {mapsLoaded ? (
            <GoogleMap
              mapContainerStyle={{ height: "100%", width: "100%" }}
              center={stateCenter}
              zoom={7} options={mapOptions}
              onLoad={onMapLoad} onUnmount={onMapUnmount}
              onClick={() => { setOpenPopupId(null); setOpenServicePopupId(null); }}
            >
              {/* 1. State boundary */}
              {stateGeoJSON && mapInstance && (
                <GeoJSONLayer
                  key={`state-boundary-${stateId}`}
                  map={mapInstance}
                  data={stateGeoJSON}
                  strokeColor="#000000"
                  strokeWeight={2.5}
                  fillColor="transparent"
                  fillOpacity={0}
                // getPopupContent={f => `<div style="font-family:sans-serif;font-size:12px"><strong>State:</strong> ${f.getProperty("name")}</div>`}
                />
              )}
              {/* 2. Region highlight */}
              {mapInstance && Array.isArray(heatmapRegionGeoJSON) && heatmapRegionGeoJSON.map((feature, i) => (
                <GeoJSONLayer
                  key={`region-highlight-${feature?.properties?.gis_id ?? i}`}
                  map={mapInstance}
                  data={feature}
                  strokeColor="#000000"
                  strokeWeight={1.5}
                  fillOpacity={activeHeatmapCondition ? 0 : (feature?.properties?.type === "state" ? 0 : 0.5)}
                  fillColor="#0341a5"
                  getHoverContent={f => `<div style="padding:2px 6px; font-weight:bold; font-family:sans-serif; font-size:13px; color:#1f2937;">${f.getProperty("name") || "Selected Region"}</div>`}
                />
              ))}
              {/* 3. Districts */}
              {mapInstance && districtGeoJSON.map((d, i) => (
                <GeoJSONLayer
                  key={`district-${d.properties?.region_id ?? i}`}
                  map={mapInstance}
                  data={d}
                  strokeColor="#000000"
                  strokeWeight={1.8}
                  fillColor="transparent"
                  fillOpacity={0}
                  getHoverContent={f => `<div style="padding:2px 6px; font-weight:bold; font-family:sans-serif; font-size:13px; color:#1f2937;">${f.getProperty("name") || "District"}</div>`}
                />
              ))}
              {/* 4. Blocks */}
              {mapInstance && (() => {
                const selectedDistricts = (Array.isArray(heatmapRegionGeoJSON) ? heatmapRegionGeoJSON : [])
                  .filter(f => f?.properties?.type === "district");

                if (selectedDistricts.length === 0) return null;

                // Collect all possible parent IDs: gis_id from heatmapRegionGeoJSON + region_id from districtGeoJSON (matched by name)
                const parentIdSet = new Set();
                selectedDistricts.forEach(f => {
                  const gisId = f?.properties?.gis_id;
                  const name = (f?.properties?.name || "").trim().toUpperCase();
                  if (gisId != null) parentIdSet.add(String(gisId));
                  // Cross-reference districtGeoJSON by name to get the region_id used by blocks
                  const matchedDistrict = districtGeoJSON.find(d =>
                    (d?.properties?.name || "").trim().toUpperCase() === name
                  );
                  if (matchedDistrict?.properties?.region_id != null) {
                    parentIdSet.add(String(matchedDistrict.properties.region_id));
                  }
                });

                const visibleBlocks = blockGeoJSON.filter(b =>
                  parentIdSet.has(String(b?.properties?.parent_region_id))
                );

                const activeCond = activeHeatmapCondition ? WEATHER_CONDITIONS.find(c => c.id === activeHeatmapCondition) : null;

                return [
                  ...visibleBlocks.map((b, i) => (
                    <GeoJSONLayer
                      key={`block-${b.properties?.region_id ?? i}`}
                      map={mapInstance}
                      data={b}
                      strokeColor="#000000ff"
                      strokeWeight={1.2}
                      fillColor="transparent"
                      fillOpacity={0}
                      getHoverContent={f => `<div style="padding:2px 6px; font-weight:bold; font-family:sans-serif; font-size:12px; color:#1f2937;">${f.getProperty("name") || "Block"}</div>`}
                    />
                  )),
                  ...visibleBlocks.map((b, i) => {
                    const centroid = computeCentroid(b.geometry);
                    if (!centroid || !activeCond || heatmapGridData.length === 0) return null;
                    const weather = findNearestWeather(centroid.lat, centroid.lng, heatmapGridData);
                    if (!weather) return null;
                    const metric = activeCond.metric(weather).split("|")[0].trim();
                    return (
                      <OverlayView
                        key={`block-weather-${b.properties?.region_id ?? i}`}
                        position={centroid}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div style={{ position: "relative" }}>
                          <div title={b.properties?.name || "Block"} style={{
                            position: "absolute",
                            transform: "translate(-50%, -50%)",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 3,
                            background: "#000000",
                            color: "#ffffff",
                            fontSize: 10,
                            fontWeight: 600,
                            padding: "3px 7px",
                            borderRadius: 4,
                            lineHeight: 1,
                            boxShadow: "0 2px 6px rgba(0,0,0,0.6)",
                            pointerEvents: "none",
                            userSelect: "none",
                            whiteSpace: "nowrap",
                            zIndex: 10,
                          }}>
                            <span style={{ fontSize: 12 }}>{activeCond.icon}</span>
                            <span>{metric}</span>
                          </div>
                        </div>
                      </OverlayView>
                    );
                  }).filter(Boolean),
                ];
              })()}

              {/* 5. Weather overlays for individually selected blocks */}
              {mapInstance && (() => {
                const selectedBlocks = (Array.isArray(heatmapRegionGeoJSON) ? heatmapRegionGeoJSON : [])
                  .filter(f => f?.properties?.type === "block");
                if (selectedBlocks.length === 0) return null;
                const activeCond = activeHeatmapCondition ? WEATHER_CONDITIONS.find(c => c.id === activeHeatmapCondition) : null;
                if (!activeCond || heatmapGridData.length === 0) return null;
                return selectedBlocks.map((b, i) => {
                  const centroid = computeCentroid(b.geometry);
                  if (!centroid) return null;
                  const weather = findNearestWeather(centroid.lat, centroid.lng, heatmapGridData);
                  if (!weather) return null;
                  const metric = activeCond.metric(weather).split("|")[0].trim();
                  return (
                    <OverlayView
                      key={`block-sel-weather-${b.properties?.gis_id ?? i}`}
                      position={centroid}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div style={{ position: "relative" }}>
                        <div title={b.properties?.name || "Block"} style={{
                          position: "absolute",
                          transform: "translate(-50%, -50%)",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 3,
                          background: "#000000",
                          color: "#ffffff",
                          fontSize: 10,
                          fontWeight: 600,
                          padding: "3px 7px",
                          borderRadius: 4,
                          lineHeight: 1,
                          boxShadow: "0 2px 6px rgba(0,0,0,0.6)",
                          pointerEvents: "none",
                          userSelect: "none",
                          whiteSpace: "nowrap",
                          zIndex: 10,
                        }}>
                          <span style={{ fontSize: 12 }}>{activeCond.icon}</span>
                          <span>{metric}</span>
                        </div>
                      </div>
                    </OverlayView>
                  );
                }).filter(Boolean);
              })()}

              {activeHeatmapCondition && (() => {
                const regionClip = heatmapRegionGeoJSON && heatmapRegionGeoJSON.length > 0 ? heatmapRegionGeoJSON : districtGeoJSON;
                return (
                  <WeatherHeatmapOverlay map={mapInstance} gridData={heatmapGridData} activeHeatmapLayer={activeHeatmapLayer} districtGeoJSON={regionClip} />
                );
              })()}
              {activeHeatmapCondition && conditionHotspots.length > 0 && !(Array.isArray(heatmapRegionGeoJSON) && heatmapRegionGeoJSON.some(f => f?.properties?.type === "district" || f?.properties?.type === "block")) && (
                <WeatherConditionOverlays map={mapInstance} conditionId={activeHeatmapCondition} hotspots={conditionHotspots} />
              )}
              {incidentMarkers.map(marker => (
                <IncidentMarker key={marker.id} marker={marker}
                  isOpen={openPopupId === marker.id}
                  onClick={() => { setOpenServicePopupId(null); setOpenPopupId(openPopupId === marker.id ? null : marker.id); }}
                  onClose={() => setOpenPopupId(null)}
                />
              ))}
              {allServiceMarkerEntries.map(({ key, place, color, accentColor, markerEmoji, label, parentId }) => (
                <ServiceMarker key={key} place={place} color={color} accentColor={accentColor}
                  markerEmoji={markerEmoji} label={label} parentId={parentId}
                  isOpen={openServicePopupId === key}
                  onClick={() => { setOpenPopupId(null); setOpenServicePopupId(openServicePopupId === key ? null : key); }}
                  onClose={() => setOpenServicePopupId(null)}
                />
              ))}
            </GoogleMap>
          ) : (
            <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#0a1628", color: "#475569", fontSize: 12 }}>Loading Map…</div>
          )}

          {isLoading && <MapLoadingOverlay />}

          {/* Heatmap loading overlay */}
          {heatmapLoading && (() => {
            const activeCond = WEATHER_CONDITIONS.find(c => c.id === activeHeatmapCondition);
            return (
              <div style={{ position: "absolute", inset: 0, zIndex: 0, background: "rgba(10,22,44,0.55)", backdropFilter: "blur(2px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, pointerEvents: "none" }}>
                <div style={{ background: "rgba(15,23,42,0.95)", borderRadius: 14, padding: "18px 28px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${activeCond ? activeCond.color + "44" : "rgba(59,130,246,0.3)"}`, border: `1.5px solid ${activeCond ? activeCond.color + "55" : "rgba(59,130,246,0.35)"}` }}>
                  <div style={{ position: "relative", width: 48, height: 48 }}>
                    {[0, 1, 2].map(i => <div key={i} style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${activeCond ? activeCond.color : "#3B82F6"}`, opacity: 0, animation: `dmRadar 1.8s ease-out ${i * 0.6}s infinite` }} />)}
                    <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 28, height: 28, borderRadius: "50%", background: activeCond ? activeCond.gradient : "linear-gradient(135deg,#3B82F6,#6366F1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, boxShadow: `0 0 12px ${activeCond ? activeCond.color + "66" : "rgba(59,130,246,0.4)"}` }}>{activeCond ? activeCond.icon : "🌡️"}</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ color: activeCond ? activeCond.color : "#60a5fa", fontSize: 13, fontWeight: 700, letterSpacing: "0.04em", marginBottom: 3 }}>{activeCond ? activeCond.label : "Weather Data"}</div>
                    <div style={{ color: "#e2e8f0", fontSize: 12, fontWeight: 600 }}>
                      Fetching Weather
                      {/* for {stateGeoJSON ? generateGridPoints(stateGeoJSON).length : "—"} locations… */}
                    </div>
                    {/* <div style={{ color: "var(--od-text-muted)", fontSize: 10, marginTop: 4 }}>This may take a moment</div> */}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Services loading overlay */}
          {loadingSubcategories.length > 0 && (() => {
            const loadingSub = ALL_SUBCATEGORIES[loadingSubcategories[0]];
            const parentCat = loadingSub ? SERVICE_CATEGORIES.find(c => c.id === loadingSub.parentId) : null;
            const accentColor = parentCat?.color || "#3B82F6";
            return (
              <div style={{ position: "absolute", inset: 0, zIndex: 0, background: "rgba(10,22,44,0.55)", backdropFilter: "blur(2px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, pointerEvents: "none" }}>
                <div style={{ background: "rgba(15,23,42,0.95)", borderRadius: 14, padding: "18px 28px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${accentColor}44`, border: `1.5px solid ${accentColor}55` }}>
                  <div style={{ position: "relative", width: 48, height: 48 }}>
                    {[0, 1, 2].map(i => <div key={i} style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${accentColor}`, opacity: 0, animation: `dmRadar 1.8s ease-out ${i * 0.6}s infinite` }} />)}
                    <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 28, height: 28, borderRadius: "50%", background: parentCat ? `linear-gradient(135deg,${accentColor},${parentCat.accentColor})` : "linear-gradient(135deg,#3B82F6,#6366F1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, boxShadow: `0 0 12px ${accentColor}66` }}>{loadingSub ? loadingSub.markerEmoji : "🏢"}</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ color: accentColor, fontSize: 13, fontWeight: 700, letterSpacing: "0.04em", marginBottom: 3 }}>{loadingSub ? loadingSub.label : "Essential Services"}</div>
                    <div style={{ color: "#e2e8f0", fontSize: 12, fontWeight: 600 }}>Searching selected regions</div>
                    <div style={{ color: "var(--od-text-muted)", fontSize: 10, marginTop: 4 }}>{loadingSubcategories.length > 1 ? `Loading ${loadingSubcategories.length} services…` : "This may take a moment"}</div>
                  </div>
                </div>
              </div>
            );
          })()}

          <style>{`@keyframes dmRadar{0%{transform:scale(0.3);opacity:0.9}100%{transform:scale(1.8);opacity:0}}`}</style>

          {showHeatmap && <HeatmapLegend activeLayer={activeHeatmapLayer} activeCondition={activeHeatmapCondition} gridData={heatmapGridData} />}

          {/* Top-right controls */}
          <div style={{ position: "absolute", top: 8, right: 10, zIndex: 0, display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
            <RegionHeatmapSelector
              options={regionDropdownOptions} loading={regionDropdownLoading}
              selectedValues={selectedHeatmapRegions} onSelect={handleHeatmapRegionSelect}
              activeCondition={activeHeatmapCondition}
              activeConditionLabel={activeHeatmapCondition ? (WEATHER_CONDITIONS.find(c => c.id === activeHeatmapCondition)?.label || activeHeatmapCondition) : null}
              isOpen={activePanel === "region"}
              onToggle={() => setActivePanel(p => p === "region" ? null : "region")}
              stateId={stateId}
              stateName={stateName}
            />
            <LayerSwitcher
              activeLayer={activeBaseLayer} onChange={setActiveBaseLayer}
              isOpen={activePanel === "layers"}
              onToggle={() => setActivePanel(p => p === "layers" ? null : "layers")}
            />
            {mapInstance && <ZoomControl map={mapInstance} />}

            {/* Fullscreen toggle button */}
            <button
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit Full Screen" : "Full Screen"}
              style={{
                width: 36, height: 36, borderRadius: 8,
                border: isFullscreen ? "2px solid #3b82f6" : "1px solid rgba(0,0,0,0.15)",
                background: isFullscreen ? "linear-gradient(135deg,#eff6ff,#dbeafe)" : "var(--od-card-bg)",
                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: isFullscreen ? "0 2px 12px rgba(59,130,246,0.35)" : "0 2px 6px rgba(0,0,0,0.15)",
                transition: "all 0.2s ease", flexShrink: 0,
              }}
              onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
              onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
            >
              {isFullscreen ? (
                /* Compress icon */
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                </svg>
              ) : (
                /* Expand icon */
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--od-text-secondary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                  </svg>
              )}
            </button>
          </div>

          {/* Weather Alerts toggle */}
          {mapsLoaded && !isLoading && (
            <>
              <button
                onClick={() => setActivePanel(prev => prev === "weather" ? null : "weather")}
                title="Weather Alerts"
                style={{ position: "absolute", top: 131, right: 10, zIndex: 0, width: 36, height: 36, borderRadius: 8, border: weatherAlertsOpen ? "2px solid #3b82f6" : enabledConditions.length > 0 ? "2px solid #f59e0b" : "1px solid rgba(0,0,0,0.15)", background: weatherAlertsOpen ? "linear-gradient(135deg,#eff6ff,#dbeafe)" : enabledConditions.length > 0 ? "linear-gradient(135deg,#fffbeb,#fef3c7)" : "var(--od-card-bg)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: weatherAlertsOpen ? "0 2px 12px rgba(59,130,246,0.35)" : enabledConditions.length > 0 ? "0 2px 12px rgba(245,158,11,0.35)" : "0 2px 6px rgba(0,0,0,0.15)", transition: "all 0.2s ease", overflow: "visible" }}
                onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
                onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
              >
                <span style={{ fontSize: 20, lineHeight: 1 }}>🔔</span>
              </button>

              {activePanel === "weather" && (
                <div style={{ position: "absolute", top: 96, right: 51, zIndex: 0, width: 252, maxHeight: "calc(100% - 110px)", overflowY: "auto", background: "var(--od-card-bg-translucent)", borderRadius: 14, boxShadow: "0 8px 32px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)", border: "1px solid var(--od-border)", backdropFilter: "blur(12px)", scrollbarWidth: "thin", animation: "waSlideIn 0.2s ease-out" }}>
                  <div style={{ padding: "10px 12px 8px", borderBottom: "1px solid var(--od-border)", display: "flex", alignItems: "center", gap: 6, position: "sticky", top: 0, background: "var(--od-card-bg-translucent)", zIndex: 1, borderRadius: "14px 14px 0 0" }}>
                    <span style={{ fontSize: 14 }}>🌡️</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--od-text-primary)" }}>Weather Alerts</div>
                      {/* {activeHeatmapCondition && <div style={{ fontSize: 9, color: "#3b82f6", fontWeight: 600 }}>{heatmapLoading ? "Loading map…" : `🗺️ ${heatmapGridData.length} pts`}</div>} */}
                    </div>
                    <button onClick={() => setActivePanel(null)} style={{ width: 22, height: 22, borderRadius: 6, border: "none", background: "var(--od-surface-alt)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "var(--od-text-muted)", flexShrink: 0, transition: "background 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--od-border)"} onMouseLeave={e => e.currentTarget.style.background = "var(--od-surface-alt)"}>✕</button>
                  </div>
                  <div style={{ padding: "6px 8px 8px" }}>
                    {WEATHER_CONDITIONS.map(cond => {
                      const isEnabled = enabledConditions.includes(cond.id);
                      const isActiveHeatmap = activeHeatmapCondition === cond.id;
                      return (
                        <label key={cond.id} onClick={() => handleToggleCondition(cond.id)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", cursor: "pointer", borderRadius: 8, background: isEnabled ? cond.bgColor : "transparent", border: isActiveHeatmap ? `1.5px solid ${cond.color}` : isEnabled ? `1px solid ${cond.borderColor}` : "1px solid transparent", marginBottom: 3, transition: "all 0.15s", boxShadow: isActiveHeatmap ? `0 0 0 2px ${cond.color}22` : "none" }}>
                          <div style={{ width: 15, height: 15, borderRadius: 4, flexShrink: 0, border: `2px solid ${isEnabled ? cond.color : "var(--od-border)"}`, background: isEnabled ? cond.color : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {isEnabled && <svg width="8" height="6" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                          </div>
                          <span style={{ fontSize: 15, flexShrink: 0 }}>{cond.icon}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: isEnabled ? cond.color : "var(--od-text-secondary)", lineHeight: 1.3 }}>{cond.label}</div>
                            <div style={{ fontSize: 12, color: "var(--od-text-muted)" }}>{cond.description}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <style>{`@keyframes waSlideIn{0%{opacity:0;transform:translateY(-8px) scale(0.97)}100%{opacity:1;transform:translateY(0) scale(1)}}`}</style>
            </>
          )}

          {!isLoading && incidentMarkers.length > 0 && (
            <IncidentBadge count={incidentMarkers.length} colorMap={categoryColorMap} />
          )}
        </div>
      </div>

      {mapsLoaded && !isLoading && !gisMap && (
        <RecentAlerts
          weatherData={weatherData}
          capitalName={capitalName}
          stateName={stateName}
          weatherLoading={weatherLoading}
          stateId={stateId}
        />
      )}

      {/* ── Essential Services Panel (separate component, called here) ── */}
      {mapsLoaded && !isLoading && !gisMap && (
        <EssentialServicesPanel
          expandedCategories={expandedCategories}
          onToggleCategory={handleToggleCategory}
          enabledSubcategories={enabledSubcategories}
          onToggleSubcategory={handleToggleSubcategory}
          subcategoryCounts={subcategoryCounts}
          loadingSubcategories={loadingSubcategories}
        />
      )}
    </div>
  );
};

export default DashboardMap;
